// ImageEditor.js

import React, { useState, useRef, useCallback, useEffect } from 'react';
import Boundary from './Boundary';
import ToolbarComponent from './ToolbarComponent';
import PrimaryToolbar from './PrimaryToolbar';
import InfoBar from './InfoBar';
import ContextualActionBar from './ContextualActionBar';
import DisplayPathPanel from './DisplayPathPanel';
import UtilityCluster from './UtilityCluster';
import LineManager from './LineManager';
import BoundaryManager from './BoundaryManager';
import ZoomPanComponent from './ZoomPanComponent';
import EditModeManager from './EditModeManager';
import Line from './Line';
import GeoInfoReader from './GeoInfoReader';
import { constrainToAngle, bearingDeg, lengthPx, hasAngleLockModifier } from './angleLock';
import RepeatLineDialog from './RepeatLineDialog';
import MoveSelectionDialog from './MoveSelectionDialog';
import { generateParallelLines } from './utils';
import jsts from 'jsts';

const HISTORY_CAP = 10;

// Serialize a Line/Boundary instance to plain data that survives deep-clone
// without losing class identity. Rehydrate via deserializeState.
// Perpendicular distance from point (px, py) to the segment (ax, ay)—(bx, by).
// Returns the distance in whatever units the inputs are in (image-px here).
const distanceToSegment = (px, py, ax, ay, bx, by) => {
  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(px - ax, py - ay);
  let t = ((px - ax) * dx + (py - ay) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const cx = ax + t * dx;
  const cy = ay + t * dy;
  return Math.hypot(px - cx, py - cy);
};

const serializeLine = (line) => ({
  id: line.id,
  points: line.points.map(p => [p[0], p[1]]),
  type: line.type,
  waypoints: line.waypoints ? line.waypoints.map(w => [w[0], w[1]]) : [],
  intersected: line.intersected,
  intersectedStart: line.intersectedStart,
  intersectedEnd: line.intersectedEnd,
  extendedStart: line.extendedStart ? [line.extendedStart[0], line.extendedStart[1]] : null,
  extendedEnd: line.extendedEnd ? [line.extendedEnd[0], line.extendedEnd[1]] : null,
  gsd: line.gsd,
  group: line.group,
});

const deserializeLine = (data) => {
  const line = new Line(data.points, data.type, data.id, data.intersected, data.gsd, data.group);
  line.waypoints = data.waypoints ? data.waypoints.map(w => [w[0], w[1]]) : [];
  line.intersectedStart = !!data.intersectedStart;
  line.intersectedEnd = !!data.intersectedEnd;
  line.extendedStart = data.extendedStart ? [data.extendedStart[0], data.extendedStart[1]] : null;
  line.extendedEnd = data.extendedEnd ? [data.extendedEnd[0], data.extendedEnd[1]] : null;
  return line;
};

const serializeBoundary = (b) => ({
  id: b.id,
  points: b.points.map(p => [p[0], p[1]]),
  isInnerBoundary: !!b.isInnerBoundary,
});

const deserializeBoundary = (data) => new Boundary(
  data.points.map(p => [p[0], p[1]]),
  data.id,
  data.isInnerBoundary,
);

const serializeState = ({ lines, boundaries, innerBoundaryPoints, completedMeasurements }) => ({
  lines: lines.map(serializeLine),
  boundaries: boundaries.map(serializeBoundary),
  innerBoundaryPoints: innerBoundaryPoints.map(p => [p[0], p[1]]),
  completedMeasurements: completedMeasurements.map(m => JSON.parse(JSON.stringify(m))),
});

const deserializeState = (snap) => ({
  lines: snap.lines.map(deserializeLine),
  boundaries: snap.boundaries.map(deserializeBoundary),
  innerBoundaryPoints: snap.innerBoundaryPoints.map(p => [p[0], p[1]]),
  completedMeasurements: snap.completedMeasurements.map(m => JSON.parse(JSON.stringify(m))),
});

// `frame` + `backdrop` make this component backdrop-agnostic.
//   Orthophoto mode: pass image/tifFile; the frame is read from the GeoTIFF by
//     GeoInfoReader exactly as before (nothing about that path changes).
//   Satellite/KML mode: pass a synthesised `frame` and a `backdrop` node. The
//     frame supplies the SAME four values GeoInfoReader would have
//     (gsd / utmZone / pixelToGeo / geoToPixel), so every tool below this line
//     -- lines, boundaries, measure, repeat, path display, export -- is
//     identical in both modes. That is the whole point: parity by sharing, not
//     by duplication.
//   [ADR: docs/20260826_satellite_kml_planning_frame.html]
const ImageEditor = ({ image, tifname, tifFile, originalImageSize, frame = null, backdrop = null }) => {
  const imageRef = useRef(null);
  const svgRef = useRef(null);
  const [isCaptureDialogOpen, setIsCaptureDialogOpen] = useState(false);
  const [captureFileName, setCaptureFileName] = useState('');
  const [currentScale, setCurrentScale] = useState(0.5);
  // Rubber-band preview while drawing: { anchor, point, locked, bearing, lengthPx }.
  const [drawPreview, setDrawPreview] = useState(null);
  const [lines, setLines] = useState([]);
  const [boundaries, setBoundaries] = useState([]);
  const [innerBoundaries, setInnerBoundaries] = useState([]);
  const [selectedLinePoint, setSelectedLinePoint] = useState(null);
  const [hoveredLine, setHoveredLine] = useState(null);
  const [hoveredLinePoint, setHoveredLinePoint] = useState(null);
  const [hoveredBoundaryPoint, setHoveredBoundaryPoint] = useState(null);
  // Transient UI feedback for Extend Line.
  const [toast, setToast] = useState(null); // { message, kind: 'info' | 'warn' }
  const [flashingLineIds, setFlashingLineIds] = useState(new Set());
  // New noun-first UI: whether the right-side Display Path panel is open.
  // The panel itself is rendered later; PrimaryToolbar just toggles this.
  const [pathPanelOpen, setPathPanelOpen] = useState(false);
  const [selectedBoundary, setSelectedBoundary] = useState(null);
  const [selectedBoundaryPoint, setSelectedBoundaryPoint] = useState(null);
  const [hoveredBoundary, setHoveredBoundary] = useState(null);
  const [editBoundaryPointIndex, setEditBoundaryPointIndex] = useState(null);
  const [currentLine, setCurrentLine] = useState(null);
  const [repeatLine, setRepeatLine] = useState(null);
  const [currentMousePos, setCurrentMousePos] = useState(null);
  const [currentBoundary, setCurrentBoundary] = useState(null);
  const [isRepeatLineDialogOpen, setIsRepeatLineDialogOpen] = useState(false);
  // ⌘⇧M / Ctrl+Shift+M — precise numeric move of the current selection. See
  // MoveSelectionDialog.js for the unit/sign convention.
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);
  // Live preview of the Repeat Line dialog's direction/distance so the
  // canvas can draw a direction arrow on the source line while the
  // dialog is open. Cleared on close/confirm.
  const [repeatPreview, setRepeatPreview] = useState(null); // { direction: 1|-1 } | null
  // History is stored as a single object { stack, index } so atomic updates
  // keep them consistent and the reducer is purely functional (safe against
  // dev-mode double-invocation).
  const [history, setHistory] = useState(() => ({
    stack: [serializeState({ lines: [], boundaries: [], innerBoundaryPoints: [], completedMeasurements: [] })],
    index: 0,
  }));
  const actionHistory = history.stack;
  const historyIndex = history.index;
  const [gsd, setGsd] = useState(null);
  const [tifFilename, setTifFilename] = useState("");
  const [geoKeys, setGeoKeys] = useState(null);
  const [selectedLine, setSelectedLine] = useState([]);
  const [pixelToGeo, setPixelToGeo] = useState(null);
  const [utmZone, setUtmZone] = useState(null);
  const [imageName, setImageName] = useState(null);
  const [displayPath, setDisplayPath] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [nodeDict, setNodeDict] = useState(null);
  const [geoToPixel, setGeoToPixel] = useState(null);
  // Last frame seen, so a frame swap can remap existing geometry onto it.
  const prevFrameRef = useRef(null);

  // When a frame is supplied there is no GeoTIFF to read, so seed the geo state
  // directly. Wrapped in `() =>` because React treats a function passed to a
  // setter as a lazy updater rather than as the value.
  useEffect(() => {
    if (!frame) return;
    setGsd(frame.gsd);
    setUtmZone(frame.utmZone);
    setPixelToGeo(() => frame.pixelToGeo);
    setGeoToPixel(() => frame.geoToPixel);
    // A synthesised frame is WGS84/UTM by construction, so the InfoBar's datum
    // chip can be filled in directly instead of sitting on "waiting…".
    setGeoKeys({ GeogCitationGeoKey: 'WGS 84' });

    // Resizing the canvas swaps in a new frame with a different origin and
    // scale, which would otherwise strand every drawn point at its old
    // coordinates. Both frames are anchored to the same UTM grid, so
    // old pixel -> UTM -> new pixel round-trips the geometry EXACTLY (to the
    // half-unit that geoToPixel rounds to). Waypoints are regenerated rather
    // than remapped because their spacing is defined in centimetres.
    const prev = prevFrameRef.current;
    prevFrameRef.current = frame;
    if (!prev || prev === frame) return;
    const remap = (pt) => {
      const [e, n] = prev.pixelToGeo(pt[0], pt[1]);
      return frame.geoToPixel(e, n);
    };
    setLines((ls) => ls.map((l) => {
      l.points = l.points.map(remap);
      if (typeof l.generateWaypoints === 'function') l.generateWaypoints(frame.gsd);
      return l;
    }));
    setBoundaries((bs) => bs.map((b) => {
      b.points = b.points.map(remap);
      return b;
    }));
    setCompletedMeasurements((ms) => ms.map((m) => ({
      ...m,
      startPoint: remap(m.startPoint),
      endPoint: remap(m.endPoint),
    })));
  }, [frame]);
  // (Removed: `innerBoundaryDistance` state. It was declared with an
  //  initial value of 50 and a comment claiming "10 feet", but nothing
  //  ever read the state and its unit never lined up with the actual
  //  inner-boundary creation call below — that one hardcodes 5 feet,
  //  converted to pixels via gsd. Leaving the dead state around
  //  invited someone to "wire it up" and silently change geometry
  //  behavior. If the inner-boundary distance needs to become
  //  configurable, add a prop with an explicit unit label (e.g.
  //  `innerBoundaryDistanceFeet`) and thread it into the
  //  `createInnerBoundary` call site.)
  const [innerBoundaryPoints, setInnerBoundaryPoints] = useState([]);
  // Inner-boundary inset (a.k.a. headland width), in FEET. Now configurable via the
  // Display Path panel (was hardcoded to 5 ft). Threaded into the createInnerBoundary calls
  // and recomputed live when it changes (see the useEffect below). Explicit unit to avoid the
  // cm/ft confusion noted in the old removed-state comment.
  const [innerBoundaryDistanceFeet, setInnerBoundaryDistanceFeet] = useState(5);
  const [showNavigationPath, setShowNavigationPath] = useState(false);
  const [nextGroupNumber, setNextGroupNumber] = useState({ inside_row: 0, end_row: 0 });
  const [selectedLines, setSelectedLines] = useState([]);
  const [turningRadius, setTurningRadius] = useState(null);
  const [preStraightDistance, setPreStraightDistance] = useState(null);
  // Transition geometry, overridable per generation. null => use the otto.yaml value,
  // so leaving these blank preserves the previous behaviour exactly.
  const [minSptDistance, setMinSptDistance] = useState(null);
  const [minSpsDistance, setMinSpsDistance] = useState(null);
  // '' = let otto.yaml decide | 'dubins' | 'reeds_shepp'
  const [turnType, setTurnType] = useState('');
  const [cuspStraightenDistance, setCuspStraightenDistance] = useState(null);
  const [useWorkspace, setUseWorkspace] = useState(false);
  // AB loop: when on, the backend generates the forward (A->B) + reverse (B->A) passes and
  // merges them into ONE looping mission (loop_path_merger) instead of a single forward pass —
  // so the operator doesn't hand-build/merge two files. Default off.
  const [abLoop, setAbLoop] = useState(false);
  // Number of A->B->A pairs when AB loop is on. 1 = the previous behaviour.
  const [abLoopCount, setAbLoopCount] = useState(1);
  const [displayImage, setDisplayImage] = useState(true);
  const [debug, setDebug] = useState(true);
  const [showShapefileLines, setShowShapefileLines] = useState(true);
  // Finished-segment length/bearing labels. Default OFF: labels on every segment turn a
  // dense layout into unreadable clutter, so the normal way to read one is to HOVER it.
  // This toggle forces them all on for auditing a whole field at once.
  const [showLineLabels, setShowLineLabels] = useState(false);
  const [workspaceBuffer, setWorkspaceBuffer] = useState(null);
  const [navigationRowSpacing, setNavigationRowSpacing] = useState(null);
  const [showUploadSection, setShowUploadSection] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [shapefileFeatures, setShapefileFeatures] = useState([]);
  const [shapefileCollection, setShapefileCollection] = useState([]);
  // Global multiplier on shapefile opacity (0–1). 1 = full intensity (current
  // behavior); lower values dim the imported shapefile lines so they recede
  // into the background and don't clutter the scene with the user's own work.
  const [shapefileOpacity, setShapefileOpacity] = useState(1);

  // Measurement state
  const [measurementPoints, setMeasurementPoints] = useState([]);
  const [completedMeasurements, setCompletedMeasurements] = useState([]);
  const [currentMeasurement, setCurrentMeasurement] = useState(null);
  // Selection/hover state for existing measurements (Select mode). Both
  // store the measurement's `id` (or null). A measurement is a simple
  // line segment, so hit-testing is "distance to segment < threshold".
  // Array of measurement ids. Multi-select via shift+click and marquee
  // mirrors the line-selection model. Empty array = nothing selected.
  const [selectedMeasurement, setSelectedMeasurement] = useState([]);
  const [hoveredMeasurement, setHoveredMeasurement] = useState(null);

  // Generate different colors for shapefiles
  const getShapefileColor = (index) => {
    const colors = [
      '#00FF00', // Green  
      '#0000FF', // Blue
      '#FF8000', // Orange
      '#8000FF', // Purple
      '#00FF80', // Cyan
      '#FF0080', // Magenta
      '#80FF00', // Lime
      '#0080FF', // Sky Blue
      '#FF8080', // Pink
      '#000000', // Black      
    ];
    return colors[index % colors.length];
  };

  const assignTypeAndGroupToSelectedLine = (type, selectedLine, nextGroupNumber) => {
    setLines(prevLines => {
      // console.log("assignTypeAndGroupToSelectedLine: selectedLine ID:", selectedLine.id);
      // console.log("assignTypeAndGroupToSelectedLine: prevLines IDs:", prevLines.map(line => line.id));
      // console.log("assignTypeAndGroupToSelectedLine: type:", type);
      
      if (type === 'inside_row' || type === 'end_row') {
        // newGroupNumber = nextGroupNumber[type];
        // console.log("assignTypeAndGroupToSelectedLine: nextGroupNumber:", nextGroupNumber);
      }
      
      const updatedLines = prevLines.map(line => {
        if (Array.isArray(selectedLine) ? selectedLine.includes(line) : line.id === selectedLine.id) {
          if (type === 'inside_row' || type === 'end_row') {
            line.setType(type);
            line.setGroup(nextGroupNumber);
          } else if (type === 'turn_row') {
            line.setType(type);
            line.setGroup(null);
          } else if (type === 'reset_row') {
            line.setType(null);
            line.setGroup(null);
          }
        }
        return line;
      });

      if (type === 'inside_row' || type === 'end_row') {
        setNextGroupNumber(prev => ({
          ...prev,
          [type]: prev[type] + 1
        }));
      }

      return updatedLines;
    });

    setSelectedLine([]);
  };

  
  const handleDisplayPath = useCallback((path) => {
    // console.log('ImageEditor: handleDisplayPath called with path:', path);
    setDisplayPath(path);
  }, []);

  const handleDisplayWorkspace = useCallback((workspace) => {
    // console.log('ImageEditor: handleDisplayWorkspace called with workspace:', workspace);
    setWorkspace(workspace);
  }, []);

  const handleDisplayNodeDict = useCallback((nodeDict) => {
    // console.log('ImageEditor: handleDisplayNodeDict called with nodeDict:', nodeDict);
    setNodeDict(nodeDict);
  }, []);

  const updateLineWaypoints = useCallback((line) => {
    if(gsd) {
      line.generateWaypoints(gsd);
    }
  }, [gsd]);

  useEffect(() => {
    lines.forEach(updateLineWaypoints);
  }, [lines, updateLineWaypoints]);

  // Recompute the inner (headland) boundary when the headland width changes, using the current
  // outer boundary. Keeps innerBoundaryPoints (row clipping) and the yellow inner Boundary object
  // (rendering) in sync. Dragging the outer boundary recomputes via its own handlers; this covers
  // changing the width alone. Depends only on the width (+gsd) to avoid a setBoundaries loop.
  useEffect(() => {
    const outer = boundaries.find(
      b => b && !b.isInnerBoundary && Array.isArray(b.points) && b.points.length >= 3);
    if (!outer || typeof gsd !== 'number' || gsd <= 0) return;
    const distPx = (1 / gsd) * innerBoundaryDistanceFeet * 30.48;
    const inner = BoundaryManager.createInnerBoundary(outer.points, distPx) || [];
    setInnerBoundaryPoints(inner);
    setBoundaries(prev => {
      const others = prev.filter(b => !b.isInnerBoundary);
      return inner.length > 0 ? [...others, new Boundary(inner, null, true)] : others;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [innerBoundaryDistanceFeet, gsd]);

  const handleUtmZoneReady = useCallback((utmZone) => {
    // console.log('ImageEditor: handleUtmZoneReady called with UTM zone:', utmZone);
    setUtmZone(utmZone);
  }, []);

  const handlePixelToGeoReady = useCallback((pixelToGeoFunc) => { 
    setPixelToGeo(() => pixelToGeoFunc);
  }, []);

  const handleGeoToPixelReady = useCallback((geoToPixelFunc) => {
    setGeoToPixel(() => geoToPixelFunc);
  }, []);


  // Build a snapshot of the current state from refs/current values.
  // Callers pass explicit values to avoid stale closure reads.
  const buildSnapshot = (linesVal, boundariesVal, innerBoundaryPointsVal, completedMeasurementsVal) =>
    serializeState({
      lines: linesVal,
      boundaries: boundariesVal,
      innerBoundaryPoints: innerBoundaryPointsVal,
      completedMeasurements: completedMeasurementsVal,
    });

  // Push a snapshot of the post-action state onto history, truncating any redo stack,
  // and cap total length at HISTORY_CAP (drop oldest).
  //
  // The reducer is a pure function of `prev` so a dev-mode double-invocation
  // is idempotent — same input, same output.
  const pushSnapshotFrom = (linesVal, boundariesVal, innerBoundaryPointsVal, completedMeasurementsVal) => {
    const snap = buildSnapshot(linesVal, boundariesVal, innerBoundaryPointsVal, completedMeasurementsVal);
    const snapKey = JSON.stringify(snap);
    setHistory(prev => {
      // Idempotent: if the incoming snapshot matches the current top, skip it.
      // This absorbs dev-mode double-invocation of this updater AND double calls
      // caused by addToHistory being invoked inside a setLines functional updater
      // (which React may also run twice in StrictMode).
      const top = prev.stack[prev.index];
      if (top && JSON.stringify(top) === snapKey) {
        return prev;
      }
      const base = prev.stack.slice(0, prev.index + 1);
      let nextStack = [...base, snap];
      if (nextStack.length > HISTORY_CAP) {
        nextStack = nextStack.slice(nextStack.length - HISTORY_CAP);
      }
      return { stack: nextStack, index: nextStack.length - 1 };
    });
  };

  // Convenience: snapshot using current state values.
  const pushSnapshot = () => {
    pushSnapshotFrom(lines, boundaries, innerBoundaryPoints, completedMeasurements);
  };

  // Coalesce rapid edits (e.g. holding an arrow key) into a single undo step.
  // Each call resets a short idle timer; when the timer fires we snapshot the
  // latest state. Also flushed immediately on undo/redo/clear and on unmount.
  const scheduleTimerRef = useRef(null);
  const scheduleSnapshot = useCallback(() => {
    if (scheduleTimerRef.current) clearTimeout(scheduleTimerRef.current);
    scheduleTimerRef.current = setTimeout(() => {
      scheduleTimerRef.current = null;
      pushSnapshotFrom(lines, boundaries, innerBoundaryPoints, completedMeasurements);
    }, 300);
  }, [lines, boundaries, innerBoundaryPoints, completedMeasurements]);

  const flushPendingSnapshot = useCallback(() => {
    if (scheduleTimerRef.current) {
      clearTimeout(scheduleTimerRef.current);
      scheduleTimerRef.current = null;
      pushSnapshotFrom(lines, boundaries, innerBoundaryPoints, completedMeasurements);
    }
  }, [lines, boundaries, innerBoundaryPoints, completedMeasurements]);

  useEffect(() => () => {
    if (scheduleTimerRef.current) clearTimeout(scheduleTimerRef.current);
  }, []);

  // Legacy shim: old callers pass {type, lines, boundaries, innerBoundaryPoints}.
  // We ignore the type and snapshot whatever values are present (falling back to
  // current state for anything missing). This keeps every existing call site working
  // while we migrate them to pushSnapshot().
  const addToHistory = (action) => {
    const linesVal = action?.lines
      ? action.lines.map(l => (l instanceof Line ? l : deserializeLine({
          id: l.id,
          points: l.points || [],
          type: l.type,
          waypoints: l.waypoints || [],
          intersected: l.intersected,
          gsd: l.gsd,
          group: l.group,
        })))
      : lines;
    const boundariesVal = action?.boundaries
      ? action.boundaries.map(b => (b instanceof Boundary ? b : deserializeBoundary({
          id: b.id,
          points: b.points || [],
          isInnerBoundary: b.isInnerBoundary,
        })))
      : boundaries;
    const innerVal = action?.innerBoundaryPoints ?? innerBoundaryPoints;
    pushSnapshotFrom(linesVal, boundariesVal, innerVal, completedMeasurements);
  };

  const [modes, setModes] = useState({
    drawingMode: false,
    editLineMode: false,
    editLinePointMode: false,
    drawingBoundaryMode: false,
    editBoundaryMode: false,
    editBoundaryPointMode: false,
    deleteLineMode: false,
    assignTypeAndGroupMode: false,
    measureDistanceMode: false,
    selectMode: false,
  });


  // ONE source of truth for the angle-lock modifier.
  //
  // A single click invokes handleImageInteraction TWICE: once from the library's
  // onPanningStop (it fires even for a click that never dragged) and once from the
  // canvas onClick. Those two callbacks receive DIFFERENT event objects and only one
  // carries shiftKey, so reading the modifier off `event` made the two invocations
  // disagree — one snapped to the bearing, the other did not, and the click produced
  // TWO points in two different places. The ref makes both agree; the duplicate-point
  // guard below then collapses them into one.
  const angleLockHeldRef = useRef(false);
  // The exact point the rubber-band preview is currently showing, in a ref.
  //
  // A click invokes handleImageInteraction TWICE (onPanningStop + onClick) and React can
  // re-render BETWEEN them. The second invocation then sees the line already extended and
  // re-snaps from the NEW endpoint, so the two calls commit two different points and a
  // duplicate check cannot catch them. Committing the preview's point makes both calls
  // produce the identical coordinate -- which the duplicate guard then collapses -- and
  // has the side benefit that a click lands exactly where the preview said it would.
  const previewPointRef = useRef(null);
  const [, setAngleLockHeld] = useState(false);
  useEffect(() => {
    const sync = (e) => { angleLockHeldRef.current = hasAngleLockModifier(e); setAngleLockHeld(hasAngleLockModifier(e)); };
    window.addEventListener('keydown', sync);
    window.addEventListener('keyup', sync);
    return () => {
      window.removeEventListener('keydown', sync);
      window.removeEventListener('keyup', sync);
    };
  }, []);

  const setMode = useCallback((modeName, value) => {
    setModes(prevModes => {
      const newModes = {
        ...Object.fromEntries(Object.keys(prevModes).map(key => [key, false])),
        [modeName]: value
      };
      if (modeName === 'drawingMode' && value) {
        setLines(prevLines => {
          // Only add a new Line if there isn't already an empty one
          if (prevLines.length === 0 || prevLines[prevLines.length - 1].points.length > 0) {
            const newLine = new Line([]);
            const updatedLines = [...prevLines, newLine];
            addToHistory({ type: 'ADD_LINE', lines: updatedLines});
            return updatedLines;
          }
          return prevLines;
        });
      }

      // Reset measurement state when entering measurement mode
      if (modeName === 'measureDistanceMode' && value) {
        console.log('Entering measurement mode - clearing measurement state');
        setCurrentMeasurement(null);
      }
      
      // Clear measurement state when exiting measurement mode
      if (modeName === 'measureDistanceMode' && !value) {
        console.log('Exiting measurement mode - clearing measurement state');
        setCurrentMeasurement(null);
      }

      if (modeName !== 'editLineMode' && modeName !== 'deleteLineMode' && modeName !== 'assignTypeAndGroupMode' && modeName !== 'editLinePointMode' && modeName !== 'drawingMode' && modeName !== 'editBoundaryMode' && modeName !== 'editBoundaryPointMode' && modeName !== 'measureDistanceMode' && modeName !== 'selectMode') {
        clearSelection();
      }
      return newModes;
    });
  }, []);

  const handleImageInteraction = useCallback((ref, event) => {
    event.stopPropagation();
    let x = event.clientX;
    let y = event.clientY;
    const rect = imageRef.current.getBoundingClientRect();
    const sourceWidth = originalImageSize?.width || imageRef.current.naturalWidth;
    const scale = sourceWidth / rect.width;
    x = Math.floor((event.clientX - rect.left) * scale);
    y = Math.floor((event.clientY - rect.top) * scale);
    
    // console.log("handleImageInteraction triggered", { x, y, currentScale, editLineMode: modes.editLineMode });

    if (!EditModeManager.isAnyModeActive(modes)) {
      return;
    }

    // Handle left-click for measurement mode
    if (modes.measureDistanceMode) {
      console.log('Measurement click detected');
      console.log('currentMeasurement state:', currentMeasurement);
      console.log('Click coordinates:', { x, y });
      
      if (currentMeasurement === null) {
        // Start new measurement
        setCurrentMeasurement({ startPoint: [x, y] });
      } else {
        console.log('Completing existing measurement');
        // Complete measurement
        const endPoint = [x, y];
        const pixelDistance = Math.sqrt(
          Math.pow(endPoint[0] - currentMeasurement.startPoint[0], 2) + 
          Math.pow(endPoint[1] - currentMeasurement.startPoint[1], 2)
        );
        const realDistance = pixelDistance * gsd; // distance in cm
        
        setCompletedMeasurements(prev => [...prev, {
          startPoint: currentMeasurement.startPoint,
          endPoint: endPoint,
          distance: realDistance,
          id: Date.now() // simple unique id
        }]);
        
        setCurrentMeasurement(null);
        console.log('Measurement completed and state cleared');
      }
      return;
    }

    if (modes.drawingMode) {
      // Angle lock: project onto the nearest 45 deg bearing from the previous point.
      // Read from THIS event so it can never act on a stale modifier state.
      if (hasAngleLockModifier(event) || angleLockHeldRef.current) {
        const pv = previewPointRef.current;
        if (pv) {
          // Commit what the preview showed. Identical for both invocations.
          x = pv[0];
          y = pv[1];
        } else {
          const inProg = lines.length ? lines[lines.length - 1] : null;
          const anchor = inProg && inProg.points.length ? inProg.points[inProg.points.length - 1] : null;
          if (anchor) {
            const c = constrainToAngle(anchor, [x, y]);
            x = c[0];
            y = c[1];
          }
        }
      }
      setLines(prevLines => {
        // Both invocations of this handler land here for one physical click. Adding the
        // same point twice was always happening; it only became visible once angle lock
        // could make the two land apart. Drop a point that repeats the previous one.
        const _last = prevLines.length ? prevLines[prevLines.length - 1] : null;
        const _lp = _last && _last.points.length ? _last.points[_last.points.length - 1] : null;
        if (_lp && Math.abs(_lp[0] - x) < 1e-6 && Math.abs(_lp[1] - y) < 1e-6) {
          return prevLines;
        }
        const newLines = [...prevLines];
        if (newLines.length === 0) {
          const newLine = new Line([[x, y]]);
          updateLineWaypoints(newLine);
          newLines.push(newLine);
          addToHistory({ type: 'ADD_LINE', lines: newLines.map(line => ({ points: line.points, id: line.id, type: line.type, waypoints: line.waypoints, gsd: line.gsd })) });
        } else {
          newLines[newLines.length - 1].addPoint([x, y]);
          updateLineWaypoints(newLines[newLines.length - 1]);
          addToHistory({ type: 'ADD_LINE_POINT', lines: newLines.map(line => ({ points: line.points, id: line.id, type: line.type, waypoints: line.waypoints, gsd: line.gsd })) });
        }
        return newLines;
      });
    }

    if (modes.editLineMode || modes.deleteLineMode || modes.assignTypeAndGroupMode) {
      // Use perpendicular-distance hit test. `scale` here is image-per-screen-px,
      // matching the hover path. Pick the geometrically nearest line (not the
      // first in array order) so behavior matches the hover highlight when
      // multiple lines qualify.
      const hitRadius = 2 * scale * 10;
      let clickedLine = null;
      let bestDist = Infinity;
      for (const line of lines) {
        const d = line.distanceTo([x, y]);
        if (d <= hitRadius && d < bestDist) {
          bestDist = d;
          clickedLine = line;
        }
      }
      if (clickedLine) {
        if (modes.deleteLineMode) {
          addToHistory({ type: 'DELETE_LINE', lines: lines.map(line => ({ points: line.points, id: line.id, type: line.type, waypoints: line.waypoints })) });
        }
        if (modes.editLineMode) {
            addToHistory({ type: 'EDIT_LINE', lines: lines.map(line => ({ points: line.points, id: line.id, type: line.type, waypoints: line.waypoints })) });
        }
      }
    }

    // assignTypeAndGroupMode click selection is handled entirely in
    // ZoomPanComponent.handleMouseDown; duplicating it here would double-toggle.

    if (modes.editLinePointMode || modes.selectMode) {
      // Hit test in image-px, using image-per-screen-px `scale` for a
      // consistent ~20 screen-pixel radius regardless of zoom.
      const threshold = 20 * scale;
      let closestPoint = null;
      let closestDistance = Infinity;
      let closestLine = null;
      lines.forEach(line => {
        line.points.forEach(p => {
          const distance = Math.hypot(p[0] - x, p[1] - y);
          if (distance < closestDistance && distance <= threshold) {
            closestDistance = distance;
            closestPoint = p;
            closestLine = line;
          }
        });
      });

      if (closestPoint) {
        setSelectedLinePoint(closestPoint);
        setSelectedLine([closestLine]);
        setSelectedMeasurement([]);
        if (modes.editLinePointMode) {
          addToHistory({ type: 'EDIT_LINE', lines: lines.map(line => ({ points: line.points, id: line.id, type: line.type, waypoints: line.waypoints })) });
        }
      } else {
        setSelectedLinePoint(null);
        // In selectMode, line selection is owned by ZoomPanComponent's
        // handleMouseDown/handleMouseUp (click-to-select and marquee).
        // Clearing selectedLine here would wipe the selection those
        // handlers just set on the same click/release cycle.
        if (!modes.selectMode) {
          setSelectedLine([]);
        }
      }
    }

    // Measurement selection in selectMode is now handled in
    // ZoomPanComponent.handleMouseDown (mirroring lines) so shift+click
    // multi-select fires before the marquee branch and the click event.
    // Don't duplicate it here — that would double-toggle the selection.

    // Boundary selection in selectMode. Only OUTER boundaries are selectable
    // — the inner is a derived inset and editing it directly would desync it
    // from its parent outer. Boundary points take priority over edges so the
    // user can grab a vertex.
    if (modes.selectMode && boundaries && boundaries.length) {
      const outerOnly = boundaries.filter(b => !b.isInnerBoundary);
      const ptThreshold = 20 * scale;
      const edgeThreshold = 10 * scale;
      let hitPoint = null;
      let hitPointIdx = -1;
      let hitPointBoundary = null;
      let bestPt = Infinity;
      for (const b of outerOnly) {
        b.points.forEach((p, idx) => {
          const d = Math.hypot(p[0] - x, p[1] - y);
          if (d < bestPt && d <= ptThreshold) {
            bestPt = d;
            hitPoint = p;
            hitPointIdx = idx;
            hitPointBoundary = b;
          }
        });
      }
      if (hitPoint) {
        setSelectedBoundaryPoint(hitPoint);
        setEditBoundaryPointIndex(hitPointIdx);
        setSelectedBoundary(hitPointBoundary);
        setSelectedLine([]);
        setSelectedLinePoint(null);
        setSelectedMeasurement([]);
      } else {
        const hitEdge = outerOnly.find(b => typeof b.isPointNear === 'function' && b.isPointNear([x, y], edgeThreshold));
        if (hitEdge) {
          setSelectedBoundary(hitEdge);
          setSelectedBoundaryPoint(null);
          setEditBoundaryPointIndex(null);
          setSelectedLine([]);
          setSelectedLinePoint(null);
          setSelectedMeasurement([]);
        } else {
          // Empty click: don't clear selectedBoundary here. ZoomPanComponent's
          // marquee handler (handleMouseUp) owns boundary marquee selection
          // in selectMode and fires just before this onClick — clearing now
          // would wipe a valid marquee result on the same gesture. Only the
          // point-level state (which marquee doesn't set) is safe to reset.
          setSelectedBoundaryPoint(null);
          setEditBoundaryPointIndex(null);
        }
      }
    }
    if (modes.drawingBoundaryMode) {
      // console.log("Drawing boundary mode is active");
      const newPoint = [x, y];
      setBoundaries(prevBoundaries => {
        let updatedBoundaries = prevBoundaries.filter(b => !b.isInnerBoundary);
        let currentOuterBoundary;

        if (updatedBoundaries.length === 0 || !currentBoundary) {
          currentOuterBoundary = new Boundary([newPoint]);
          updatedBoundaries = [currentOuterBoundary];
          setCurrentBoundary(currentOuterBoundary);
        } else {
          currentOuterBoundary = updatedBoundaries[updatedBoundaries.length - 1];
          currentOuterBoundary.addPoint(newPoint);
        }
        let innerPoints = [];
        if (currentOuterBoundary.points.length >= 3) {
          // Inner boundary is inset by 5 feet from the outer boundary.
          // Distance is passed in pixels: 5 ft × 30.48 cm/ft / gsd (cm/px).
          // Hardcoded until a real use-case shows up — see the comment at
          // the former `innerBoundaryDistance` state for context. If you
          // make this configurable, add a prop with an explicit unit
          // (e.g. `innerBoundaryDistanceFeet`) rather than a unitless
          // number, since past attempts conflated cm and feet.
          innerPoints = BoundaryManager.createInnerBoundary(currentOuterBoundary.points, (1 / gsd) * innerBoundaryDistanceFeet * 30.48);
          // console.log("Inner boundary points:", innerPoints);
          setInnerBoundaryPoints(innerPoints);

          if (innerPoints && innerPoints.length > 0) {
            const innerBoundary = new Boundary(innerPoints, null, true);
            updatedBoundaries.push(innerBoundary);
          }
        }

        addToHistory({ 
          type: 'ADD_BOUNDARY_POINT', 
          boundaries: updatedBoundaries.map(boundary => ({ 
            points: boundary.points, 
            id: boundary.id, 
            type: boundary.type, 
            isInnerBoundary: boundary.isInnerBoundary 
          })),
          innerBoundaryPoints: innerPoints
        });
        return updatedBoundaries;
      });
    }

    if (modes.editBoundaryMode) {
      // console.log("Edit boundary mode is active");
      // console.log("Hovered boundary:", hoveredBoundary ? hoveredBoundary.id : "none");
      // console.log("Selected boundary before:", selectedBoundary ? selectedBoundary.id : "none");
      const clickedBoundary = boundaries.find(boundary => boundary.isPointNear([x, y], 20 / currentScale));    
      if (clickedBoundary) {
        // console.log("Clicked on boundary:", clickedBoundary.id);
        setSelectedBoundary(clickedBoundary);
      } else {
        // console.log("Clicked outside any boundary");
        setSelectedBoundary(null);
      }

      // console.log("Selected boundary after:", clickedBoundary ? clickedBoundary.id : "none");
    }
    if (modes.editBoundaryPointMode) {
      let closestPoint = null;
      let closestDistance = Infinity;
      let closestBoundary = null;
      let closestPointIndex = -1;
      boundaries.forEach(boundary => {
        boundary.points.forEach((p, index) => {
          const distance = Math.sqrt(Math.pow(p[0] - x, 2) + Math.pow(p[1] - y, 2));
          if (distance < closestDistance && distance <= 500 / currentScale) {
            closestDistance = distance;
            closestPoint = p;
            closestBoundary = boundary;
            closestPointIndex = index;
          }
        });
      });
      if (closestPoint) {
        setSelectedBoundaryPoint(closestPoint);
        setEditBoundaryPointIndex(closestPointIndex);
        // Track the owning boundary so the arrow-key handler (which
        // requires both selectedBoundary and selectedBoundaryPoint) can
        // actually move the right point.
        setSelectedBoundary(closestBoundary);
      }
    }
  }, [modes, currentScale, lines, currentLine, boundaries, selectedBoundaryPoint, hoveredBoundary, currentBoundary, updateLineWaypoints, gsd, modes.assignTypeAndGroupMode, currentMeasurement, completedMeasurements]);


  useEffect(() => {
    // console.log("Inner boundary points:", innerBoundaryPoints);
    setBoundaries(prevBoundaries => {
      const outerBoundaries = prevBoundaries.filter(b => !b.isInnerBoundary);
      if (innerBoundaryPoints && innerBoundaryPoints.length > 0) {
        const newInnerBoundary = new Boundary(innerBoundaryPoints, null, true);
        // console.log("New inner boundary:", newInnerBoundary);
        return [...outerBoundaries, newInnerBoundary];
      }
      return outerBoundaries;
    });
  }, [innerBoundaryPoints]);


  useEffect(() => {
    // console.log("Boundaries changed:", boundaries);
  }, [boundaries]);

  const handleMouseMove = useCallback((event, { x, y, scale }) => {
    setCurrentMousePos({ x, y });

    // Rubber-band preview. Anchor is the last point of the in-progress line, so the
    // operator sees where the next click will land -- and, when the modifier is held,
    // the exact bearing and length before committing.
    if (modes.drawingMode) {
      const inProg = lines.length ? lines[lines.length - 1] : null;
      const anchor = inProg && inProg.points.length ? inProg.points[inProg.points.length - 1] : null;
      angleLockHeldRef.current = hasAngleLockModifier(event);
      const locked = angleLockHeldRef.current;
      const target = (locked && anchor) ? constrainToAngle(anchor, [x, y]) : [x, y];
      previewPointRef.current = (locked && anchor) ? target : null;
      setDrawPreview(anchor
        ? { anchor, point: target, locked: !!locked,
            bearing: bearingDeg(anchor, target), lengthPx: lengthPx(anchor, target) }
        : null);
    } else if (drawPreview) {
      setDrawPreview(null);
      previewPointRef.current = null;
    }

    // if (modes.measureDistanceMode && currentMeasurement) {
    //   console.log('Mouse move in measurement mode:', { x, y, currentMeasurement });
    // }

    // `scale` here is image-pixels-per-screen-pixel. Target ~20 screen-px hit
    // radius → 20 * scale image-pixels. isPointNear multiplies its threshold
    // by 10 internally, so pass (2 * scale).
    const lineHitThreshold = 2 * scale;
    const pointHitThreshold = 20 * scale; // image-px

    // Pan (the neutral state, no mode flag set) is included so hovering a finished line
    // reveals its length/bearing without first switching tools -- otherwise the
    // hover-to-reveal label would look broken in the mode operators sit in by default.
    const _neutralMode = !Object.values(modes).some(Boolean);
    if (modes.editLineMode || modes.deleteLineMode || modes.assignTypeAndGroupMode || modes.selectMode || _neutralMode) {
      // Pick the geometrically nearest line within the hit threshold, not the
      // first line that happens to qualify. When lines are close together and
      // the view is zoomed out, multiple lines can pass the threshold test;
      // first-in-array order was causing hover/click mismatches.
      const hitRadius = lineHitThreshold * 10; // isPointNear multiplies by 10 internally
      // In selectMode, suppress line hover when the cursor is already over a
      // point on that line — the point hover takes precedence so users can
      // pick either a point or a whole line without flicker.
      const nearPoint = modes.selectMode && (() => {
        for (const line of lines) {
          for (const p of line.points) {
            if (Math.hypot(p[0] - x, p[1] - y) <= pointHitThreshold) return true;
          }
        }
        return false;
      })();
      let closestLine = null;
      let bestDist = Infinity;
      if (!nearPoint) {
        for (const line of lines) {
          const d = line.distanceTo([x, y]);
          if (d <= hitRadius && d < bestDist) {
            bestDist = d;
            closestLine = line;
          }
        }
      }
      setHoveredLine(closestLine);
    } else if (!modes.editLinePointMode) {
      if (hoveredLine) setHoveredLine(null);
    }

    // Hover highlight for individual line points in editLinePointMode or selectMode.
    if (modes.editLinePointMode || modes.selectMode) {
      let nearest = null;
      let best = Infinity;
      for (const line of lines) {
        for (const p of line.points) {
          const d = Math.hypot(p[0] - x, p[1] - y);
          if (d < best && d <= pointHitThreshold) {
            best = d;
            nearest = p;
          }
        }
      }
      setHoveredLinePoint(nearest);
    } else if (hoveredLinePoint) {
      setHoveredLinePoint(null);
    }

    // Hover highlight for completed measurements in selectMode. Threshold
    // is image-px, aimed at ~10 screen-px around the measurement segment.
    if (modes.selectMode && completedMeasurements && completedMeasurements.length) {
      const measHitThreshold = 10 * scale;
      let nearestId = null;
      let best = Infinity;
      for (const m of completedMeasurements) {
        if (!m.startPoint || !m.endPoint) continue;
        const d = distanceToSegment(x, y, m.startPoint[0], m.startPoint[1], m.endPoint[0], m.endPoint[1]);
        if (d < best && d <= measHitThreshold) {
          best = d;
          nearestId = m.id;
        }
      }
      setHoveredMeasurement(nearestId);
    } else if (hoveredMeasurement) {
      setHoveredMeasurement(null);
    }

    // Hover highlight for individual boundary points.
    if (modes.editBoundaryPointMode || modes.selectMode) {
      let nearest = null;
      let best = Infinity;
      // Outer-only — inner boundaries aren't editable, so don't surface
      // hover affordance there.
      for (const b of boundaries) {
        if (b.isInnerBoundary) continue;
        for (const p of b.points) {
          const d = Math.hypot(p[0] - x, p[1] - y);
          if (d < best && d <= pointHitThreshold) {
            best = d;
            nearest = p;
          }
        }
      }
      setHoveredBoundaryPoint(nearest);
    } else if (hoveredBoundaryPoint) {
      setHoveredBoundaryPoint(null);
    }

    if (modes.editBoundaryMode || modes.editBoundaryPointMode) {
      const boundaryHitThreshold = 2 * scale;
      const closestBoundary = boundaries.reduce((closest, boundary) => {
        return boundary.isPointNear([x, y], boundaryHitThreshold) ? boundary : closest;
      }, null);
      
      // console.log("Closest boundary:", closestBoundary ? closestBoundary.id : "none");
      
      setHoveredBoundary(closestBoundary);
      
      if (modes.editBoundaryMode && closestBoundary) {
        setSelectedBoundary(closestBoundary);
      }
    }
  }, [modes, lines, boundaries, currentMeasurement, hoveredLine, hoveredLinePoint, hoveredBoundaryPoint, completedMeasurements, hoveredMeasurement]);

  const disableAllModes = useCallback(() => {
    setModes(prevModes => {
      const newModes = { ...prevModes };
      Object.keys(newModes).forEach(key => {
        newModes[key] = false;
      });
      return newModes;
    });
  }, []);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Escape') {
      // Clear active measurement if in measure distance mode
      if (modes.measureDistanceMode && currentMeasurement) {
        setCurrentMeasurement(null);
        return;
      }
      
      disableAllModes();
      setSelectedLine([]);
      setSelectedBoundary(null);  
      return;
    }

    const currentMode = EditModeManager.getCurrentMode(modes);
    // Hold Shift for fast moves (10× the normal step). Lets the user
    // make big edits without spamming the arrow keys.
    const moveMultiplier = event.shiftKey ? 10 : 1;

    if (currentMode === 'editLineMode' && selectedLine) {
      selectedLine.forEach(line => {
        LineManager.handleEditLine(event.key, line.id, lines, setLines, moveMultiplier);
        // Moving the whole line invalidates both extension anchors.
        if (typeof line.resetIntersectedFlag === 'function') line.resetIntersectedFlag();
      });
      scheduleSnapshot();
    }

    if (currentMode === 'deleteLineMode' && selectedLine) {
      selectedLine.forEach(line => { deleteLine(line.id); });
      scheduleSnapshot();
    }
    if (currentMode === 'editLineMode' && selectedLine.length > 1) {
      selectedLine.forEach(line => {
        LineManager.handleEditLine(event.key, line.id, lines, setLines, moveMultiplier);
        if (typeof line.resetIntersectedFlag === 'function') line.resetIntersectedFlag();
      });
      scheduleSnapshot();
    }
    if (currentMode === 'deleteLineMode' && selectedLine.length > 1) {
      selectedLine.forEach(line => deleteLine(line.id));
      scheduleSnapshot();
    }

    if (currentMode === 'editLinePointMode' && selectedLinePoint) {
      // Figure out which end the selected point is (if either) before the edit
      // so we can reset the right flag. If the point is in the middle, neither
      // flag changes.
      const owningLine = lines.find(l => l.points.some(p => p[0] === selectedLinePoint[0] && p[1] === selectedLinePoint[1]));
      let whichEnd = null;
      if (owningLine) {
        const idx = owningLine.points.findIndex(p => p[0] === selectedLinePoint[0] && p[1] === selectedLinePoint[1]);
        if (idx === 0) whichEnd = 'start';
        else if (idx === owningLine.points.length - 1) whichEnd = 'end';
      }
      LineManager.handleEditLinePoint(event.key, selectedLinePoint, lines, setLines, setSelectedLinePoint, moveMultiplier);
      if (owningLine && whichEnd && typeof owningLine.resetIntersectedFlag === 'function') {
        owningLine.resetIntersectedFlag(whichEnd);
      }
      scheduleSnapshot();
    }
    // Boundary edits operate ONLY on the outer boundary. The inner is a
    // derived inset and gets recomputed from the new outer points so it
    // visually drags along with the outer. Editing an inner directly is
    // a no-op (we filter out inners in selectMode anyway, but guard
    // here too in case some path leaves an inner selected).
    const ARROW_DIR = {
      ArrowUp:    { dx: 0,  dy: -1 },
      ArrowDown:  { dx: 0,  dy:  1 },
      ArrowLeft:  { dx: -1, dy:  0 },
      ArrowRight: { dx: 1,  dy:  0 },
    };
    if (currentMode === 'editBoundaryMode' && selectedBoundary && !selectedBoundary.isInnerBoundary) {
      const dir = ARROW_DIR[event.key];
      if (dir) {
        const step = 10 * moveMultiplier;
        const outer = boundaries.find(b => b.id === selectedBoundary.id);
        if (outer) {
          const newPts = outer.points.map(([x, y]) => [x + dir.dx * step, y + dir.dy * step]);
          const distPx = (typeof gsd === 'number' && gsd > 0) ? (1 / gsd) * innerBoundaryDistanceFeet * 30.48 : 0;
          const newInner = (distPx > 0 && newPts.length >= 3)
            ? (BoundaryManager.createInnerBoundary(newPts, distPx) || [])
            : [];
          setBoundaries(prev => prev.map(b => b.id === outer.id
            ? new Boundary(newPts, b.id, false)
            : b));
          setInnerBoundaryPoints(newInner);
          // Refresh the selection ref so subsequent presses act on the moved boundary.
          setSelectedBoundary(new Boundary(newPts, outer.id, false));
          scheduleSnapshot();
        }
      }
    }
    if (currentMode === 'editBoundaryPointMode' && selectedBoundaryPoint && selectedBoundary && editBoundaryPointIndex !== null && !selectedBoundary.isInnerBoundary) {
      const dir = ARROW_DIR[event.key];
      if (dir) {
        const step = 10 * moveMultiplier;
        const outer = boundaries.find(b => b.id === selectedBoundary.id);
        if (outer) {
          const newPts = outer.points.map((p, i) => i === editBoundaryPointIndex
            ? [p[0] + dir.dx * step, p[1] + dir.dy * step]
            : p);
          const distPx = (typeof gsd === 'number' && gsd > 0) ? (1 / gsd) * innerBoundaryDistanceFeet * 30.48 : 0;
          const newInner = (distPx > 0 && newPts.length >= 3)
            ? (BoundaryManager.createInnerBoundary(newPts, distPx) || [])
            : [];
          setBoundaries(prev => prev.map(b => b.id === outer.id
            ? new Boundary(newPts, b.id, false)
            : b));
          setInnerBoundaryPoints(newInner);
          // Track the moved point so the highlight follows it.
          const movedPt = newPts[editBoundaryPointIndex];
          setSelectedBoundaryPoint(movedPt);
          setSelectedBoundary(new Boundary(newPts, outer.id, false));
          scheduleSnapshot();
        }
      }
    }
  }, [modes, selectedLine, selectedLinePoint, selectedBoundary, selectedBoundaryPoint, lines, boundaries, editBoundaryPointIndex, currentMousePos, disableAllModes, currentMeasurement, scheduleSnapshot, gsd]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Cmd/Ctrl+Z = undo, Shift+Cmd/Ctrl+Z (or Cmd/Ctrl+Y) = redo.
  // Ignore when focus is on a text input so the user's typing isn't hijacked.
  useEffect(() => {
    const onShortcut = (event) => {
      const mod = event.metaKey || event.ctrlKey;
      if (!mod) return;
      const tag = event.target?.tagName;
      const isEditable = tag === 'INPUT' || tag === 'TEXTAREA' || event.target?.isContentEditable;
      if (isEditable) return;
      const key = event.key.toLowerCase();
      if (key === 'z' && !event.shiftKey) {
        event.preventDefault();
        undo();
      } else if ((key === 'z' && event.shiftKey) || key === 'y') {
        event.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', onShortcut);
    return () => window.removeEventListener('keydown', onShortcut);
  }, [actionHistory, historyIndex, lines, boundaries, innerBoundaryPoints, completedMeasurements]);

  // State for the shortcut help overlay. Declared here (unconditional
  // hook order) even though the effect that uses it is moved down
  // below handleContextualDelete/Edit so those callbacks exist when
  // the effect registers them as dependencies.
  const [shortcutsHelpOpen, setShortcutsHelpOpen] = useState(false);

  // Mirror of the legacy ToolbarComponent's path-calc state, derived by
  // observing the legacy "Display Path" button's text content. The button
  // switches to "Calculating... 42%" during work; we parse that so the new
  // DisplayPathPanel can render matching progress + disable its Apply
  // button instead of silently queuing duplicate clicks. Temporary bridge:
  // goes away once the calc is lifted into this component and driven
  // from real state. Observer scope is `document.body` because the legacy
  // button lives inside a portal-ish off-canvas mount.
  const [pathCalc, setPathCalc] = useState({ isCalculating: false, progress: 0 });
  useEffect(() => {
    // Find the legacy button lazily; it may mount after us. The legacy
    // ToolbarComponent tags its Display Path button with
    // `data-bridge="display-path"` so we can locate it without brittle
    // text-regex matching (breaks if label changes or another button ever
    // shares that text).
    const findBtn = () => document.querySelector('button[data-bridge="display-path"]');
    let btn = findBtn();
    let textObserver = null;
    const read = () => {
      const b = btn || findBtn();
      if (!b) { setPathCalc({ isCalculating: false, progress: 0 }); return; }
      const txt = (b.textContent || '').trim();
      const m = txt.match(/^Calculating\.{0,3}\s*([\d.]+)%/i);
      if (m) setPathCalc({ isCalculating: true, progress: parseFloat(m[1]) });
      else setPathCalc({ isCalculating: false, progress: 0 });
    };
    const attachTextObserver = (b) => {
      if (!b || textObserver) return;
      textObserver = new MutationObserver(read);
      textObserver.observe(b, { characterData: true, childList: true, subtree: true });
    };
    // Watch the DOM for the legacy button appearing/disappearing so we can
    // (re)attach the text observer. Debounced via rAF because
    // childList+subtree fires on every render — the button identity check
    // itself is cheap but we don't need same-frame accuracy for mount/unmount.
    let rafId = 0;
    const bodyObserver = new MutationObserver(() => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        const next = findBtn();
        if (next !== btn) {
          if (textObserver) { textObserver.disconnect(); textObserver = null; }
          btn = next;
          attachTextObserver(btn);
          read();
        }
      });
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
    attachTextObserver(btn);
    read();
    return () => {
      if (textObserver) textObserver.disconnect();
      bodyObserver.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  // Ref mirror of `lines` so async callbacks (e.g. the deferred snapshot push
  // in extendSelectedLines) can read the post-commit value without closure staleness.
  const linesRef = useRef(lines);
  useEffect(() => {
    linesRef.current = lines;
  }, [lines]);

  // Auto-dismiss toast after 4s.
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);


  // Restore state from the snapshot at the given history index. Index 0 is the
  // initial snapshot (seeded on first mount). Clears selection if the restored
  // state no longer contains it. Reads from history/actionHistory in closure
  // (not from inside a functional updater) so side effects only fire once,
  // even under React dev-mode double-invocation.
  const restoreSnapshot = (targetIndex) => {
    if (targetIndex < 0 || targetIndex >= actionHistory.length) return;
    const snap = actionHistory[targetIndex];
    const restored = deserializeState(snap);
    setLines(restored.lines);
    setBoundaries(restored.boundaries);
    setInnerBoundaryPoints(restored.innerBoundaryPoints);
    setCompletedMeasurements(restored.completedMeasurements);

    // Re-point selection to the restored Line/Boundary instances by id
    // (selection must not hold stale pre-undo references). Drop entries that
    // no longer exist.
    setSelectedLine(prev => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      const byId = new Map(restored.lines.map(l => [l.id, l]));
      return prev.map(l => byId.get(l.id)).filter(Boolean);
    });
    setSelectedBoundary(prev => {
      if (!prev) return prev;
      const byId = new Map(restored.boundaries.map(b => [b.id, b]));
      return byId.get(prev.id) || null;
    });
    setSelectedLinePoint(null);
    setSelectedBoundaryPoint(null);
    setCurrentMeasurement(null);
    setMeasurementPoints([]);
    // Clear the Extend-line flash if an undo/redo fires during the 2s window.
    setFlashingLineIds(new Set());

    // Pure functional index update — returns identity when the target already
    // matches so React can bail out.
    setHistory(prev => (prev.index === targetIndex ? prev : { stack: prev.stack, index: targetIndex }));
  };

  const undo = () => {
    flushPendingSnapshot();
    if (historyIndex > 0) {
      restoreSnapshot(historyIndex - 1);
    }
  };

  const redo = () => {
    flushPendingSnapshot();
    if (historyIndex < actionHistory.length - 1) {
      restoreSnapshot(historyIndex + 1);
    }
  };

  const removePoint = (lineId, index) => {
    setLines(prevLines => {
      const updatedLines = prevLines.map(line => 
        line.id === lineId ? new Line(line.points.filter((_, i) => i !== index)) : line
      );
      addToHistory({ type: 'EDIT_LINE', lines: updatedLines.map(line => ({ points: line.points, id: line.id })) });
      return updatedLines;
    });
  };
  
  const deleteLine = (lineId) => {
    setLines(prevLines => {
      const updatedLines = prevLines.filter(line => line.id !== lineId);
      return updatedLines;
    });
  };

  // const createNewBoundary = () => {
  //   const newBoundary = new Boundary([]);
  //   setBoundaries(prevBoundaries => {
  //     const updatedBoundaries = [...prevBoundaries, newBoundary];
  //     addToHistory({ type: 'ADD_BOUNDARY', boundaries: updatedBoundaries });
  //     return updatedBoundaries;
  //   });
  // };
  
  const addBoundaryPoint = (boundaryId, point) => {
    setBoundaries(prevBoundaries => {
      const updatedBoundaries = prevBoundaries.map(boundary => 
        boundary.id === boundaryId ? { ...boundary, points: [...boundary.points, point] } : boundary
      );
      addToHistory({ type: 'EDIT_BOUNDARY', boundaries: updatedBoundaries });
      return updatedBoundaries;
    });
  };
  
  const removeBoundaryPoint = (boundaryId, index) => {
    setBoundaries(prevBoundaries => {
      const updatedBoundaries = prevBoundaries.map(boundary => 
        boundary.id === boundaryId ? { ...boundary, points: boundary.points.filter((_, i) => i !== index) } : boundary
      );
      addToHistory({ type: 'EDIT_BOUNDARY', boundaries: updatedBoundaries });
      return updatedBoundaries;
    });
  };
  
  const deleteBoundary = (boundaryId) => {
    setBoundaries(prevBoundaries => {
      const target = prevBoundaries.find(b => b.id === boundaryId);
      // If the user deleted the outer, also drop the inner — it's a derived
      // inset and would otherwise linger as an orphan dashed-yellow polygon.
      // Clearing innerBoundaryPoints triggers the useEffect at the inner-
      // rebuild site to remove the inner Boundary from the array; do it
      // here too so the local update is consistent in one tick.
      const dropInner = target && target.isInnerBoundary === false;
      const updatedBoundaries = prevBoundaries.filter(boundary => {
        if (boundary.id === boundaryId) return false;
        if (dropInner && boundary.isInnerBoundary) return false;
        return true;
      });
      addToHistory({ type: 'DELETE_BOUNDARY', boundaries: updatedBoundaries });
      if (dropInner) setInnerBoundaryPoints([]);
      return updatedBoundaries;
    });
    // Clear the in-progress drawing reference and any selection that
    // pointed at the deleted boundary. Without this, re-entering Boundary
    // mode would treat the next click as continuing the deleted polygon
    // (since `currentBoundary` still pointed at the now-removed instance),
    // and selection state would still flag the deleted feature.
    setCurrentBoundary(null);
    setSelectedBoundary(null);
    setSelectedBoundaryPoint(null);
    setEditBoundaryPointIndex(null);
    setHoveredBoundary(null);
    setHoveredBoundaryPoint(null);
  };

  // Selection-aware handlers for the new ContextualActionBar. These
  // intentionally live at the same level as the individual deleteLine /
  // deleteBoundary helpers so they can call them directly, avoid prop
  // drilling, and coexist with the legacy ToolbarComponent buttons.
  //
  // Edit: turn ON the mode that matches the current selection shape. The
  // legacy setMode already zeroes every other mode in one call, so we
  // don't have to worry about orphaned modes.
  const handleContextualEdit = useCallback(() => {
    if (selectedLinePoint) {
      setMode('editLinePointMode', true);
      return;
    }
    if (selectedBoundaryPoint) {
      setMode('editBoundaryPointMode', true);
      return;
    }
    if (selectedBoundary) {
      setMode('editBoundaryMode', true);
      return;
    }
    if (Array.isArray(selectedLine) && selectedLine.length >= 1) {
      setMode('editLineMode', true);
      return;
    }
  }, [selectedLine, selectedLinePoint, selectedBoundary, selectedBoundaryPoint, setMode]);

  // Delete: removes whatever is selected. For a single line/boundary point
  // we rely on the existing remove*Point path (boundary) or splice the line
  // points array (line). For feature-level selections we reuse the existing
  // deleteLine / deleteBoundary helpers. All routes snapshot history.
  const handleContextualDelete = useCallback(() => {
    // Priority mirrors ContextualActionBar.resolveKind.
    if (Array.isArray(selectedMeasurement) && selectedMeasurement.length > 0) {
      const idsToDelete = new Set(selectedMeasurement);
      setCompletedMeasurements(prev => prev.filter(m => !idsToDelete.has(m.id)));
      setSelectedMeasurement([]);
      return;
    }
    if (selectedLinePoint) {
      // Remove the selected line-point from whichever line owns it.
      setLines(prevLines => {
        const updated = prevLines.map(line => {
          const idx = line.points.findIndex(p => p[0] === selectedLinePoint[0] && p[1] === selectedLinePoint[1]);
          if (idx === -1) return line;
          const newLine = new Line(
            line.points.filter((_, i) => i !== idx),
            line.type,
            line.id,
            line.intersected,
            line.gsd,
            line.group,
          );
          newLine.waypoints = line.waypoints;
          return newLine;
        });
        addToHistory({ type: 'DELETE_LINE_POINT', lines: updated });
        return updated;
      });
      setSelectedLinePoint(null);
      return;
    }
    if (selectedBoundaryPoint && selectedBoundary && editBoundaryPointIndex !== null) {
      removeBoundaryPoint(selectedBoundary.id, editBoundaryPointIndex);
      setSelectedBoundaryPoint(null);
      return;
    }
    if (selectedBoundary) {
      deleteBoundary(selectedBoundary.id);
      setSelectedBoundary(null);
      return;
    }
    if (Array.isArray(selectedLine) && selectedLine.length >= 1) {
      selectedLine.forEach(line => deleteLine(line.id));
      // Snapshot once for the whole batch.
      setLines(prev => {
        addToHistory({ type: 'DELETE_LINE', lines: prev });
        return prev;
      });
      setSelectedLine([]);
      return;
    }
  }, [selectedLine, selectedLinePoint, selectedBoundary, selectedBoundaryPoint, editBoundaryPointIndex, selectedMeasurement]);

  // ---------------------------------------------------------------------
  // Precise numeric move (⌘⇧M / Ctrl+Shift+M)
  // ---------------------------------------------------------------------
  // True when the canvas holds anything movable. Measurements are excluded
  // deliberately — they're annotations over the imagery, not path geometry,
  // so sliding them would desync them from what they measure.
  // A boundary POINT is always accompanied by its parent boundary (see
  // handleImageInteraction), so the boundary check covers both cases —
  // testing selectedBoundaryPoint on its own would only ever produce a
  // false positive that opens the dialog on nothing movable.
  const hasMovableSelection =
    (Array.isArray(selectedLine) && selectedLine.length > 0) ||
    !!selectedLinePoint ||
    (!!selectedBoundary && !selectedBoundary.isInnerBoundary);

  // Human-readable summary shown in the dialog header so the user can
  // confirm WHAT is about to move before committing an invisible offset.
  const moveSelectionLabel = (() => {
    const parts = [];
    if (selectedLinePoint) parts.push('1 line point');
    else if (Array.isArray(selectedLine) && selectedLine.length > 0) {
      parts.push(`${selectedLine.length} line${selectedLine.length > 1 ? 's' : ''}`);
    }
    if (selectedBoundaryPoint) parts.push('1 boundary point');
    else if (selectedBoundary && !selectedBoundary.isInnerBoundary) parts.push('1 boundary');
    return parts.join(' + ');
  })();

  // Apply the dialog's pixel delta to every selected feature.
  //
  // Point-vs-feature precedence matters: clicking a line point ALSO sets
  // `selectedLine` to its owning line (see handleImageInteraction), so
  // moving both would shift that vertex twice. When a point is selected we
  // therefore move the point ONLY. The line branch and the boundary branch
  // run independently of each other, though — a marquee can legitimately
  // select lines AND the outer boundary together, and the user expects the
  // whole set to move as one.
  const handleMoveSelection = useCallback(({ dxPx, dyPx }) => {
    if (!isFinite(dxPx) || !isFinite(dyPx)) return;
    if (dxPx === 0 && dyPx === 0) return;

    // ---- lines / line points ----
    // Both branches mutate the Line objects in place and hand setLines a new
    // ARRAY. That's the pattern LineManager.handleEditLine already uses, and
    // it matters for two reasons beyond consistency:
    //   - `scheduleSnapshot`'s debounced closure captures the pre-call `lines`
    //     array; because the objects inside it are the ones we mutated, the
    //     undo snapshot records the moved geometry rather than the old.
    //   - `serializeLine` deep-copies points at capture time, so mutating in
    //     place cannot corrupt snapshots already on the history stack.
    if (selectedLinePoint) {
      setLines(prevLines => prevLines.map(line => {
        const pointIndex = line.points.findIndex(p => p[0] === selectedLinePoint[0] && p[1] === selectedLinePoint[1]);
        if (pointIndex === -1) return line;
        line.updatePoint(pointIndex, dxPx, dyPx);
        // Moving an endpoint invalidates only THAT end's extension anchor;
        // an interior vertex leaves both anchors valid.
        if (typeof line.resetIntersectedFlag === 'function') {
          if (pointIndex === 0) line.resetIntersectedFlag('start');
          else if (pointIndex === line.points.length - 1) line.resetIntersectedFlag('end');
        }
        return line;
      }));
      setSelectedLinePoint([selectedLinePoint[0] + dxPx, selectedLinePoint[1] + dyPx]);
    } else if (Array.isArray(selectedLine) && selectedLine.length > 0) {
      const movingIds = new Set(selectedLine.map(l => l.id));
      setLines(prevLines => prevLines.map(line => {
        if (!movingIds.has(line.id)) return line;
        line.move(dxPx, dyPx);
        // Translating the whole line invalidates BOTH extension anchors.
        if (typeof line.resetIntersectedFlag === 'function') line.resetIntersectedFlag();
        return line;
      }));
      // Waypoints regenerate on their own — the `lines.forEach(updateLineWaypoints)`
      // effect fires whenever setLines hands back a new array.
    }

    // ---- boundary / boundary points ----
    // Only the OUTER boundary is editable; the inner is a derived inset and
    // gets recomputed from the new outer so it follows along.
    const outer = selectedBoundary && !selectedBoundary.isInnerBoundary
      ? boundaries.find(b => b.id === selectedBoundary.id)
      : null;
    if (outer) {
      const movingWholeBoundary = !selectedBoundaryPoint || editBoundaryPointIndex === null;
      // Mutate in place for the same snapshot-correctness reason as the lines
      // above (serializeBoundary also deep-copies, so history stays intact).
      if (movingWholeBoundary) outer.move(dxPx, dyPx);
      else outer.updatePoint(editBoundaryPointIndex, dxPx, dyPx);

      const distPx = (typeof gsd === 'number' && gsd > 0) ? (1 / gsd) * innerBoundaryDistanceFeet * 30.48 : 0;
      const newInner = (distPx > 0 && outer.points.length >= 3)
        ? (BoundaryManager.createInnerBoundary(outer.points, distPx) || [])
        : [];
      setBoundaries(prev => [...prev]);
      setInnerBoundaryPoints(newInner);
      // Re-point the selection refs at the moved geometry so a follow-up move
      // (or the readout) doesn't act on stale coordinates.
      setSelectedBoundary(outer);
      if (!movingWholeBoundary) setSelectedBoundaryPoint(outer.points[editBoundaryPointIndex]);
    }

    scheduleSnapshot();
    setIsMoveDialogOpen(false);
  }, [
    selectedLine, selectedLinePoint, selectedBoundary, selectedBoundaryPoint,
    editBoundaryPointIndex, boundaries, gsd, innerBoundaryDistanceFeet,
    // `lines` is not referenced directly (the update runs inside setLines'
    // functional form), but scheduleSnapshot closes over it, so this callback
    // still refreshes whenever the line set changes.
    scheduleSnapshot,
  ]);

  // ⌘⇧M (mac) / Ctrl+Shift+M (windows) opens the move dialog. Registered
  // separately from the single-key shortcut effect below, which bails out on
  // any modifier key. Requires Select mode + a movable selection.
  //
  // Shift is part of the chord because plain ⌘M is the macOS "Minimize
  // window" command — AppKit can swallow it before the page sees it.
  useEffect(() => {
    const onKey = (event) => {
      if (!(event.metaKey || event.ctrlKey)) return;
      if (event.altKey || !event.shiftKey) return;
      if (event.key.toLowerCase() !== 'm') return;
      const tag = event.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || event.target?.isContentEditable) return;
      if (isRepeatLineDialogOpen || isCaptureDialogOpen) return;
      if (!modes.selectMode || !hasMovableSelection) return;
      event.preventDefault();
      setIsMoveDialogOpen(open => !open);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modes.selectMode, hasMovableSelection, isRepeatLineDialogOpen, isCaptureDialogOpen]);

  // A move dialog left open over a selection that has since been cleared
  // would apply to nothing — close it instead of silently no-op'ing.
  useEffect(() => {
    if (isMoveDialogOpen && !hasMovableSelection) setIsMoveDialogOpen(false);
  }, [isMoveDialogOpen, hasMovableSelection]);

  // Keyboard shortcuts for the noun-first UI. All single-key (no
  // modifier) so they feel immediate; gated on "not typing" and "no
  // edit mode active" so they don't collide with:
  //   - text input in dialogs / the capture filename field
  //   - arrow-key nudge + Delete behavior inside editLine*/editBoundary*
  //     modes (those are handled by handleKeyDown above)
  //   - WASD pan in ZoomPanComponent (letters here don't overlap)
  //
  // Shortcut map:
  //   V                - Pan / Select (neutral mode)
  //   B                - Boundary tool
  //   L                - Line tool
  //   M                - Measure tool
  //   E                - Edit the current selection
  //   Del / Backspace  - Delete the current selection
  //   ?                - Toggle the shortcut help overlay
  //
  // Lives AFTER handleContextualEdit/Delete because it captures them in
  // its dependency array; earlier placement triggers the const TDZ.
  useEffect(() => {
    const onKey = (event) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const tag = event.target?.tagName;
      const isEditable = tag === 'INPUT' || tag === 'TEXTAREA' || event.target?.isContentEditable;
      if (isEditable) return;
      if (isRepeatLineDialogOpen || isCaptureDialogOpen || isMoveDialogOpen) return;

      const k = event.key;
      const lower = k.toLowerCase();
      const currentMode = EditModeManager.getCurrentMode(modes);
      // A mode is "canvas-active" when the canvas is already handling
      // keystrokes for the current mode via `handleKeyDown` (arrow-key
      // nudges, or legacy deleteLineMode's click-to-delete + Delete-key
      // flow). We must NOT also fire contextual shortcuts in those modes,
      // or Delete double-fires (once via handleKeyDown, once via
      // handleContextualDelete). Allow-list beats regex here — the old
      // `/^edit/` regex silently missed `deleteLineMode` (still
      // reachable via the off-canvas legacy toolbar).
      const CANVAS_ACTIVE_MODES = new Set([
        'editLineMode',
        'editLinePointMode',
        'editBoundaryMode',
        'editBoundaryPointMode',
        'deleteLineMode',
      ]);
      const canvasActive = currentMode && CANVAS_ACTIVE_MODES.has(currentMode);

      if (!canvasActive) {
        if (lower === 'v') {
          event.preventDefault();
          // Toggle: Pan ↔ Select. setMode already zeroes every other mode,
          // so any active drawing/edit mode turns off on the first press
          // and the next press flips back to Pan.
          setMode('selectMode', !modes.selectMode);
          return;
        }
        if (lower === 'b') { event.preventDefault(); setMode('drawingBoundaryMode', !modes.drawingBoundaryMode); return; }
        if (lower === 'l') { event.preventDefault(); setMode('drawingMode', !modes.drawingMode); return; }
        if (lower === 'm') { event.preventDefault(); setMode('measureDistanceMode', !modes.measureDistanceMode); return; }
      }

      const hasSelection =
        (Array.isArray(selectedLine) && selectedLine.length > 0) ||
        selectedLinePoint || selectedBoundary || selectedBoundaryPoint ||
        (Array.isArray(selectedMeasurement) && selectedMeasurement.length > 0);
      if (hasSelection && !canvasActive) {
        if (k === 'Delete' || k === 'Backspace') { event.preventDefault(); handleContextualDelete(); return; }
        if (lower === 'e') { event.preventDefault(); handleContextualEdit(); return; }
      }

      if (k === '?' || (event.shiftKey && lower === '/')) {
        event.preventDefault();
        setShortcutsHelpOpen(o => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    modes,
    selectedLine, selectedLinePoint, selectedBoundary, selectedBoundaryPoint, selectedMeasurement,
    isRepeatLineDialogOpen, isCaptureDialogOpen, isMoveDialogOpen,
    disableAllModes, setMode,
    handleContextualDelete, handleContextualEdit,
  ]);

  // Assign Type: reuses the existing assignTypeAndGroupToSelectedLine
  // helper. Works for 1 OR many selected lines — it already accepts either
  // a single line or an array, and derives the next group number itself.
  const handleContextualAssignType = useCallback((type) => {
    if (!type || !Array.isArray(selectedLine) || selectedLine.length === 0) return;
    const filtered = lines.filter(line => line.type === type);
    const nextGroup = filtered.length === 0 ? 1 : (Math.max(...filtered.map(l => l.group || 0)) + 1);
    selectedLine.forEach(line => {
      assignTypeAndGroupToSelectedLine(type, line, nextGroup);
    });
    setSelectedLine([]);
  }, [selectedLine, lines]);

  // Repeat Line now lives here (lifted from ToolbarComponent). The
  // dialog state (`isRepeatLineDialogOpen`, declared near the top of
  // this file) is now the source of truth, rather than being mirrored
  // from the legacy toolbar. Previously this was a DOM-bridge that
  // clicked the old button by text match; that bridge has been
  // removed.
  //
  // Resolve which line drives Repeat Line:
  //   0 selected   -> last non-empty line
  //   1 selected   -> that line (prefer the live reference in `lines`
  //                   because selection can be a stale snapshot)
  //   >1 selected  -> null + 'too-many' mode (caller warns + aborts)
  const resolveRepeatSource = useCallback(() => {
    const selCount = Array.isArray(selectedLine) ? selectedLine.length : 0;
    const nonEmpty = lines.filter(line => line.points.length > 1);
    if (selCount === 0) {
      return { line: nonEmpty[nonEmpty.length - 1] || null, mode: 'last' };
    }
    if (selCount === 1) {
      const sel = selectedLine[0];
      const live = lines.find(l => l.id === sel.id) || sel;
      if (!live || !live.points || live.points.length < 2) {
        return { line: null, mode: 'selected-invalid' };
      }
      return { line: live, mode: 'selected' };
    }
    return { line: null, mode: 'too-many' };
  }, [selectedLine, lines]);

  const flashRepeatSource = useCallback((lineId) => {
    if (!lineId) return;
    setFlashingLineIds(new Set([lineId]));
    setTimeout(() => setFlashingLineIds(new Set()), 2000);
  }, []);

  const handleContextualRepeat = useCallback(() => {
    const { line: sourceLine, mode } = resolveRepeatSource();
    if (mode === 'too-many') {
      setToast({ message: 'Select exactly one line to repeat, or clear selection to repeat the last line.', kind: 'warn' });
      return;
    }
    if (!sourceLine) {
      setToast({ message: 'No line available to repeat.', kind: 'warn' });
      return;
    }
    flashRepeatSource(sourceLine.id);
    setIsRepeatLineDialogOpen(true);
  }, [resolveRepeatSource, flashRepeatSource]);

  const handleConfirmRepeat = useCallback(({ gsd: gsdArg, count, distance, direction }) => {
    if (lines.length === 0) return;
    const parsedGsd = parseFloat(gsdArg);
    const parsedCount = parseInt(count);
    const parsedDistance = parseFloat(distance);
    const parsedDirection = parseInt(direction);
    if (isNaN(parsedGsd) || isNaN(parsedCount) || isNaN(parsedDistance) || isNaN(parsedDirection)) {
      console.error('Invalid Repeat Line input values');
      return;
    }

    // Resolve source at CONFIRM time (selection may have changed between
    // opening the dialog and clicking confirm).
    const { line: sourceLine, mode: sourceMode } = resolveRepeatSource();
    if (!sourceLine) {
      if (sourceMode === 'too-many') {
        setToast({ message: 'Select exactly one line to repeat, or clear selection to repeat the last line.', kind: 'warn' });
      } else {
        setToast({ message: 'No line available to repeat.', kind: 'warn' });
      }
      setIsRepeatLineDialogOpen(false);
      return;
    }

    const parallelLines = generateParallelLines(sourceLine, parsedCount, parsedGsd, parsedDirection, parsedDistance);

    // Clip generated lines against existing lines, then against the
    // inner boundary if one exists.
    const parallelLinesClipped = parallelLines.map(line => {
      const newLine = new Line(line.points);
      newLine.clipToOtherLines(lines);
      return newLine;
    });

    let clippedLines;
    if (innerBoundaryPoints.length > 0) {
      const geometryFactory = new jsts.geom.GeometryFactory();
      // Close the polygon if it isn't already.
      const pts = innerBoundaryPoints.slice();
      if (pts.length >= 3) {
        const first = pts[0];
        const last = pts[pts.length - 1];
        if (first[0] !== last[0] || first[1] !== last[1]) pts.push(first);
        const shellCoords = pts.map(p => new jsts.geom.Coordinate(p[0], p[1]));
        const shell = geometryFactory.createLinearRing(shellCoords);
        const innerBoundaryPolygon = geometryFactory.createPolygon(shell);
        clippedLines = parallelLinesClipped.map(line => {
          const newLine = new Line(line.points);
          newLine.clipToInnerBoundary(innerBoundaryPolygon);
          return newLine;
        });
      }
    }

    const appended = clippedLines || parallelLinesClipped;
    const newLinesForHistory = [...lines, ...appended];
    setLines(newLinesForHistory);
    addToHistory({
      type: 'REPEAT_LINE',
      lines: newLinesForHistory.map(line => ({
        points: line.points,
        id: line.id,
        type: line.type,
        waypoints: line.waypoints,
        gsd: line.gsd,
        group: line.group,
      })),
    });
    setRepeatLine(true);
    setIsRepeatLineDialogOpen(false);
    EditModeManager.disableAllModes((value) => Object.keys(modes).forEach(m => setMode(m, value)));
    setSelectedBoundary(null);
  }, [lines, innerBoundaryPoints, modes, resolveRepeatSource]);

  const handleGsdCalculated = useCallback((calculatedGsd) => {
    // console.log("handleGsdCalculated called in ImageEditor with:", calculatedGsd);
    setGsd(calculatedGsd);
  }, []);

  const handleGeoKeysCalculated = useCallback((calculatedGeoKeys) => {
    if (calculatedGeoKeys) {
      // Diagnostic: log the full geoKeys object once so we can see
      // whether `GeogCitationGeoKey` / `GTCitationGeoKey` are the
      // actually-populated keys for this image's GeoTIFF encoding, or
      // whether the data lives under different tags (PCSCitationGeoKey,
      // etc). Different GeoTIFF writers use different subsets of the
      // spec — the InfoBar reads the two citation keys; if they're
      // undefined we need to fall back to alternatives.
      // eslint-disable-next-line no-console
      console.log('[InfoBar] geoKeys received:', calculatedGeoKeys);
      setGeoKeys(calculatedGeoKeys);
    }
  }, []);


  useEffect(() => {
    if (tifname) {
      // console.log("tifname in ImageEditor:", tifname);
      setTifFilename(tifname)
      setImageName(tifname)
    }
  }, [tifname]);

  // Extend Line: operates on the current selection. Each selected line's open
  // endpoints get extended to the nearest target (any other line on canvas,
  // inner/outer boundary, or image edge as ultimate backstop). Lines that are
  // already flagged intersected on both ends are no-ops. Pushes one undo
  // snapshot for the whole batch and flashes the modified lines orange for 2s.
  const extendSelectedLines = useCallback(() => {
    if (!selectedLine || selectedLine.length === 0) {
      setToast({ message: 'Select one or more lines before extending.', kind: 'warn' });
      return;
    }
    const imageBounds = {
      width: originalImageSize?.width || imageRef.current?.naturalWidth || 0,
      height: originalImageSize?.height || imageRef.current?.naturalHeight || 0,
    };
    // Tolerance for "already on a target" check: scale with image size so it
    // behaves at any zoom. ~0.1% of the larger dimension, min 2 px.
    const tolerance = Math.max(2, Math.max(imageBounds.width, imageBounds.height) * 0.001);

    const selectedIds = new Set(selectedLine.map(l => l.id));
    const outerBoundaryPoints = (boundaries.find(b => !b.isInnerBoundary)?.points) || [];

    // Compute everything against a frozen snapshot of `lines` at the time of
    // the click. This avoids React StrictMode double-invocation issues (since
    // the work happens outside any functional updater) and ensures the
    // "other lines" reference used for each extension is the original,
    // pre-batch geometry — not the mutated intermediate state from earlier
    // iterations.
    const originalLines = lines;

    // Clone every line so we don't mutate the instances currently held in
    // state (which would also appear in past undo snapshots if those shared
    // references — they don't today because snapshots deserialize new
    // instances, but cloning is still the safe default).
    const clonedLines = originalLines.map(l => deserializeLine(serializeLine(l)));
    const cloneById = new Map(clonedLines.map(l => [l.id, l]));

    let totalEndpoints = 0;
    let extendedCount = 0;
    const modifiedIds = new Set();

    for (const original of originalLines) {
      if (!selectedIds.has(original.id)) continue;
      const target = cloneById.get(original.id);
      // Other-lines pool is the ORIGINAL geometry of every other line
      // (selected or not). Using clones here would cascade extensions within
      // one batch in a surprising way.
      const others = originalLines.filter(l => l.id !== original.id);
      if (!target.intersectedStart) totalEndpoints++;
      if (!target.intersectedEnd) totalEndpoints++;
      const r = target.extendOpenEndpoints(others, innerBoundaryPoints, outerBoundaryPoints, imageBounds, tolerance);
      if (r.startExtended) extendedCount++;
      if (r.endExtended) extendedCount++;
      if (r.startExtended || r.endExtended) {
        modifiedIds.add(target.id);
        if (typeof target.generateWaypoints === 'function' && gsd) {
          target.generateWaypoints(gsd);
        }
      }
    }

    const failed = totalEndpoints - extendedCount;
    if (totalEndpoints === 0) {
      setToast({ message: 'Nothing to extend — all selected endpoints were already set.', kind: 'info' });
      return;
    }
    if (failed > 0) {
      setToast({ message: `Extended ${extendedCount} of ${totalEndpoints} endpoints. ${failed} couldn't be extended.`, kind: 'warn' });
    } else {
      setToast({ message: `Extended ${extendedCount} endpoint${extendedCount === 1 ? '' : 's'}.`, kind: 'info' });
    }

    if (extendedCount === 0) {
      // Nothing changed; don't touch state or history.
      return;
    }

    // Commit the new lines array. Passing a plain array (not a functional
    // updater) avoids React double-invocation; the clones are fresh instances
    // so no in-place mutation of prior state occurs.
    setLines(clonedLines);

    // Push a single undo snapshot using the computed post-state directly.
    // We don't read from React state via closure/refs — pushSnapshotFrom
    // accepts the values it should snapshot.
    pushSnapshotFrom(clonedLines, boundaries, innerBoundaryPoints, completedMeasurements);

    // Flash modified lines orange for 2s.
    if (modifiedIds.size > 0) {
      setFlashingLineIds(modifiedIds);
      setTimeout(() => setFlashingLineIds(new Set()), 2000);
    }

    // Re-point selectedLine entries to the new cloned instances so subsequent
    // operations (including another Extend click) operate on the current
    // Line objects, not the stale originals.
    setSelectedLine(prev => (prev || []).map(l => cloneById.get(l.id) || l));
  }, [selectedLine, lines, innerBoundaryPoints, boundaries, originalImageSize, gsd, completedMeasurements]);

  const handleUploadShapeFile = () => {
    setShowUploadSection(true);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
  };

  const handleFileUpload = async () => {
    if (selectedFile) {
      console.log("Uploading file:", selectedFile.name);
      
      try {
        // Read the file as ArrayBuffer
        const arrayBuffer = await selectedFile.arrayBuffer();
        const dataView = new DataView(arrayBuffer);
        
        // Parse the shapefile header (first 100 bytes)
        const fileCode = dataView.getInt32(0, false); // Big endian
        const fileLength = dataView.getInt32(24, false); // Big endian (in 16-bit words)
        const version = dataView.getInt32(28, true); // Little endian
        const shapeType = dataView.getInt32(32, true); // Little endian
        
        // Extract bounding box
        const xMin = dataView.getFloat64(36, true);
        const yMin = dataView.getFloat64(44, true);
        const xMax = dataView.getFloat64(52, true);
        const yMax = dataView.getFloat64(60, true);
        
        const geoInfo = {
          fileCode,
          fileLength,
          version,
          shapeType,
          boundingBox: { xMin, yMin, xMax, yMax },
          fileName: selectedFile.name,
          fileSize: selectedFile.size
        };
        
        console.log("=== SHAPEFILE HEADER ===");
        console.log("Extracted geo information:", geoInfo);
        console.log("Shape Type:", getShapeTypeName(shapeType));
        console.log("Bounding Box:", `X: ${xMin} to ${xMax}, Y: ${yMin} to ${yMax}`);
        
        // Parse shape records starting after header (byte 100)
        console.log("\n=== PARSING SHAPE RECORDS ===");
        const shapes = parseShapeRecords(dataView, 100);
        console.log(`Found ${shapes.length} shape records:`);
        
        // Convert shapefile coordinates from UTM to pixel coordinates
        if (geoToPixel && typeof geoToPixel === 'function') {
          console.log("\n=== CONVERTING TO PIXEL COORDINATES ===");
          const pixelFeatures = convertShapesToPixelCoordinates(shapes);
          setShapefileFeatures(pixelFeatures);
          console.log("Converted shapes to pixel coordinates:", pixelFeatures);
          
          // Create Line objects for display (similar to how you handle drawn lines)
          const shapefileLines = createLinesFromShapes(pixelFeatures, shapeType);
          if (shapefileLines.length > 0) {
            const newShapefile = {
              id: Date.now(), // Unique identifier
              filename: selectedFile.name,
              uploadTime: new Date().toLocaleTimeString(),
              lines: shapefileLines,
              color: getShapefileColor(shapefileCollection.length),
              segmentCount: Math.ceil(shapefileLines.length / 2),
              originalShapeType: shapeType,
              visible: true
            };
            
            setShapefileCollection(prevCollection => [...prevCollection, newShapefile]);
            console.log(`Added ${shapefileLines.length} lines from shapefile '${selectedFile.name}' with color ${newShapefile.color}`);
          }
        } else {
          console.warn("geoToPixel function not available - cannot convert to pixel coordinates");
        }
        
        shapes.forEach((shape, index) => {
          console.log(`\n--- Shape ${index + 1} ---`);
          console.log("Record Number:", shape.recordNumber);
          console.log("Shape Type:", getShapeTypeName(shape.shapeType));
          console.log("Geometry:", shape.geometry);
          
          if (shape.geometry) {
            if (shape.shapeType === 1) { // Point
              console.log(`Point coordinates: (${shape.geometry.x}, ${shape.geometry.y})`);
            } else if (shape.shapeType === 3) { // Polyline
              console.log(`Polyline with ${shape.geometry.numParts} parts and ${shape.geometry.numPoints} points`);
              console.log("Bounding box:", shape.geometry.boundingBox);
              console.log("Points:", shape.geometry.points.slice(0, 5), shape.geometry.points.length > 5 ? "..." : "");
            } else if (shape.shapeType === 5) { // Polygon
              console.log(`Polygon with ${shape.geometry.numParts} parts and ${shape.geometry.numPoints} points`);
              console.log("Bounding box:", shape.geometry.boundingBox);
              console.log("Points:", shape.geometry.points.slice(0, 5), shape.geometry.points.length > 5 ? "..." : "");
            }
          }
        });
        
        console.log(`\n=== SUMMARY ===`);
        console.log(`Total shapes: ${shapes.length}`);
        console.log(`File size: ${selectedFile.size} bytes`);
        
      } catch (error) {
        console.error("Error parsing shapefile:", error);
      }
      
      // Close the upload section
      setShowUploadSection(false);
      setSelectedFile(null);
    }
  };

  // Convert shapefile coordinates from UTM to ENU, then to pixel coordinates
  const convertShapesToPixelCoordinates = (shapes) => {
    return shapes.map(shape => {
      if (!shape.geometry) return shape;
      
      let convertedGeometry = { ...shape.geometry };
      
      if (shape.shapeType === 1) { // Point
        // Pass UTM coordinates directly to geoToPixel (TIFF georeferencing expects UTM)
        const [pixelX, pixelY] = geoToPixel(shape.geometry.x, shape.geometry.y);
        convertedGeometry = {
          x: pixelX,
          y: pixelY,
          originalX: shape.geometry.x,
          originalY: shape.geometry.y,
          utmX: shape.geometry.x,
          utmY: shape.geometry.y
        };
      } else if (shape.shapeType === 3 || shape.shapeType === 5) { // Polyline or Polygon
        convertedGeometry.points = shape.geometry.points.map(point => {
          // Pass UTM coordinates directly to geoToPixel (TIFF georeferencing expects UTM)
          const [pixelX, pixelY] = geoToPixel(point.x, point.y);
          return {
            x: pixelX,
            y: pixelY,
            originalX: point.x,
            originalY: point.y,
            utmX: point.x,
            utmY: point.y
          };
        });
      }
      
      return {
        ...shape,
        geometry: convertedGeometry
      };
    });
  };

  // Helper function to calculate direction change between three points
  const calculateAngleBetweenPoints = (p1, p2, p3) => {
    const vector1 = { x: p1.x - p2.x, y: p1.y - p2.y };
    const vector2 = { x: p3.x - p2.x, y: p3.y - p2.y };
    
    const dot = vector1.x * vector2.x + vector1.y * vector2.y;
    const mag1 = Math.sqrt(vector1.x * vector1.x + vector1.y * vector1.y);
    const mag2 = Math.sqrt(vector2.x * vector2.x + vector2.y * vector2.y);
    
    if (mag1 === 0 || mag2 === 0) return 0;
    
    const cosAngle = dot / (mag1 * mag2);
    const angleRad = Math.acos(Math.max(-1, Math.min(1, cosAngle)));
    const angleDeg = (angleRad * 180) / Math.PI;
    
    // Convert to direction change (deviation from straight line)
    // Straight line = 180° between vectors = 0° direction change
    // 90° turn = 90° between vectors = 90° direction change
    const directionChange = Math.abs(180 - angleDeg);
    
    return directionChange;
  };

  // Detect row boundaries based on direction changes
  const detectRowBoundaries = (points, angleThreshold = 1) => {
    if (points.length < 3) return [points];
    
    const segments = [];
    let currentSegment = [points[0]];
    
    for (let i = 1; i < points.length - 1; i++) {
      const angle = calculateAngleBetweenPoints(points[i-1], points[i], points[i+1]);
      
      if (angle > angleThreshold) {
        // Significant direction change detected - end current segment
        currentSegment.push(points[i]);
        segments.push([...currentSegment]); // Create copy of current segment
        currentSegment = [points[i]]; // Start new segment with the turning point
        // console.log(`  Direction change detected at point ${i}: ${angle.toFixed(1)}° - Creating new segment`);
      } else {
        currentSegment.push(points[i]);
      }
    }
    
    // Add the last point and final segment
    currentSegment.push(points[points.length - 1]);
    segments.push(currentSegment);
    
    return segments;
  };

  // Create Line objects from shapefile features for display
  const createLinesFromShapes = (pixelFeatures, shapeType) => {
    const lines = [];
    
    pixelFeatures.forEach((feature, index) => {
      if (!feature.geometry) return;
      
      if (shapeType === 1) { // Point
        // For points, create a small cross or marker
        const x = feature.geometry.x;
        const y = feature.geometry.y;
        const size = 10; // 10 pixel marker size
        const pointLine = new Line([
          [x - size, y],
          [x + size, y],
          [x, y],
          [x, y - size],
          [x, y + size]
        ]);
        pointLine.setType('shapefile_point');
        pointLine.isFromShapefile = true;
        lines.push(pointLine);
      } else if (shapeType === 3) { // Polyline
        console.log(`\n=== PROCESSING POLYLINE FEATURE ${index + 1} ===`);
        const allPoints = feature.geometry.points;
        console.log(`Original polyline has ${allPoints.length} points`);
        console.log(`Original polyline all points: ${allPoints}`);
        
        if (allPoints.length >= 3) {
          // Segment the polyline based on direction changes
          const segments = detectRowBoundaries(allPoints, 2); // 5 degree threshold
          console.log(`Detected ${segments.length} segments based on direction changes:`);
          
          segments.forEach((segment, segmentIndex) => {
            if (segment.length >= 2) {
              const firstPoint = segment[0];
              const lastPoint = segment[segment.length - 1];
              
              // console.log(`  Segment ${segmentIndex + 1}: ${segment.length} points`);
              // console.log(`    First point: (${firstPoint.x.toFixed(1)}, ${firstPoint.y.toFixed(1)})`);
              // console.log(`    Last point: (${lastPoint.x.toFixed(1)}, ${lastPoint.y.toFixed(1)})`);
              
              // Create simplified line with first and last points of this segment
              const simplifiedPoints = [[firstPoint.x, firstPoint.y], [lastPoint.x, lastPoint.y]];
              
              const line = new Line(simplifiedPoints);
              line.setType('shapefile_polyline');
              line.isFromShapefile = true;
              line.originalPointCount = segment.length;
              line.featureIndex = index;
              line.segmentIndex = segmentIndex;
              lines.push(line);
              
              // console.log(`    Created simplified line for segment ${segmentIndex + 1}`);
            }
          });
        } else if (allPoints.length === 2) {
          // If only 2 points, create line directly
          const firstPoint = allPoints[0];
          const lastPoint = allPoints[1];
          const simplifiedPoints = [[firstPoint.x, firstPoint.y], [lastPoint.x, lastPoint.y]];
          
          const line = new Line(simplifiedPoints);
          line.setType('shapefile_polyline');
          line.isFromShapefile = true;
          line.originalPointCount = 2;
          line.featureIndex = index;
          line.segmentIndex = 0;
          lines.push(line);
          
          console.log(`Created direct line from 2-point polyline`);
        }
      }
      // Skip polygons for now as requested
    });
    
    console.log(`\n=== SEGMENTATION SUMMARY ===`);
    console.log(`Total lines created from shapefile: ${lines.length}`);
    return lines;
  };

  // Function to parse shape records from the shapefile
  const parseShapeRecords = (dataView, startOffset) => {
    const shapes = [];
    let offset = startOffset;
    
    try {
      while (offset < dataView.byteLength - 8) { // Ensure we have at least 8 bytes left for record header
        // Read record header
        const recordNumber = dataView.getInt32(offset, false); // Big endian
        const contentLength = dataView.getInt32(offset + 4, false); // Big endian (in 16-bit words)
        const contentLengthBytes = contentLength * 2; // Convert to bytes
        
        if (offset + 8 + contentLengthBytes > dataView.byteLength) {
          break; // Not enough data left
        }
        
        // Read shape type for this record
        const shapeType = dataView.getInt32(offset + 8, true); // Little endian
        
        let geometry = null;
        
        // Parse geometry based on shape type
        if (shapeType === 0) {
          // Null shape - no geometry
          geometry = null;
        } else if (shapeType === 1) {
          // Point
          geometry = parsePoint(dataView, offset + 12);
        } else if (shapeType === 3) {
          // Polyline
          geometry = parsePolyline(dataView, offset + 12);
        } else if (shapeType === 5) {
          // Polygon
          geometry = parsePolygon(dataView, offset + 12);
        }
        
        shapes.push({
          recordNumber,
          contentLength,
          shapeType,
          geometry
        });
        
        // Move to next record
        offset += 8 + contentLengthBytes;
      }
    } catch (error) {
      console.warn("Error parsing shape record:", error);
    }
    
    return shapes;
  };

  // Parse Point geometry
  const parsePoint = (dataView, offset) => {
    return {
      x: dataView.getFloat64(offset, true),
      y: dataView.getFloat64(offset + 8, true)
    };
  };

  // Parse Polyline geometry
  const parsePolyline = (dataView, offset) => {
    // Bounding box (32 bytes)
    const boundingBox = {
      xMin: dataView.getFloat64(offset, true),
      yMin: dataView.getFloat64(offset + 8, true),
      xMax: dataView.getFloat64(offset + 16, true),
      yMax: dataView.getFloat64(offset + 24, true)
    };
    
    // Number of parts and points
    const numParts = dataView.getInt32(offset + 32, true);
    const numPoints = dataView.getInt32(offset + 36, true);
    
    // Parts array (starting points of each part)
    const parts = [];
    for (let i = 0; i < numParts; i++) {
      parts.push(dataView.getInt32(offset + 40 + i * 4, true));
    }
    
    // Points array
    const points = [];
    const pointsOffset = offset + 40 + numParts * 4;
    for (let i = 0; i < numPoints; i++) {
      points.push({
        x: dataView.getFloat64(pointsOffset + i * 16, true),
        y: dataView.getFloat64(pointsOffset + i * 16 + 8, true)
      });
    }
    
    return {
      boundingBox,
      numParts,
      numPoints,
      parts,
      points
    };
  };

  // Parse Polygon geometry (same structure as Polyline)
  const parsePolygon = (dataView, offset) => {
    return parsePolyline(dataView, offset); // Polygons have the same structure as polylines
  };

  // Helper function to get shape type name
  const getShapeTypeName = (shapeType) => {
    const shapeTypes = {
      0: "Null Shape",
      1: "Point",
      3: "Polyline", 
      5: "Polygon",
      8: "MultiPoint",
      11: "PointZ",
      13: "PolylineZ",
      15: "PolygonZ",
      18: "MultiPointZ",
      21: "PointM",
      23: "PolylineM",
      25: "PolygonM",
      28: "MultiPointM",
      31: "MultiPatch"
    };
    return shapeTypes[shapeType] || `Unknown (${shapeType})`;
  };

  const handleCancelUpload = () => {
    setShowUploadSection(false);
    setSelectedFile(null);
  };


  const defineRowGroups = () => {
    // console.log("defineRowGroups called in ImageEditor");
  };

  const clearSelection = () => {
    setSelectedLine([]);
    setSelectedBoundary(null);
    setSelectedBoundaryPoint(null);
  };
  const onClearAllMeasurements = useCallback(() => {
    console.log("onClearAllMeasurements called in ImageEditor");
    setCompletedMeasurements([]);
    setCurrentMeasurement(null);
    // Snapshot the post-clear state (empty measurements, unchanged lines/boundaries)
    // so the clear itself is undoable.
    pushSnapshotFrom(lines, boundaries, innerBoundaryPoints, []);
  }, [lines, boundaries, innerBoundaryPoints]);

  // Clear all markers/lines/boundaries and push a snapshot so the clear is undoable.
  const clearAllMarkers = useCallback(() => {
    setLines([]);
    setBoundaries([]);
    setInnerBoundaryPoints([]);
    setSelectedLine([]);
    setSelectedBoundary(null);
    setSelectedBoundaryPoint(null);
    setHoveredBoundary(null);
    setCurrentBoundary(null);
    pushSnapshotFrom([], [], [], completedMeasurements);
  }, [completedMeasurements]);

  const handleTurningRadiusChange = (event) => {
    setTurningRadius(event.target.value);
  };

  const handlePreStraightDistanceChange = (event) => {
    setPreStraightDistance(event.target.value);
  };

  const handleUseWorkspaceChange = (event) => {
    setUseWorkspace(event.target.checked);
  };

  const handleAbLoopChange = (event) => {
    setAbLoop(event.target.checked);
  };

  const handleDisplayImageChange = (event) => {
    setDisplayImage(event.target.checked);
  };

  const handleDisplayPathChange = (event) => {
    setShowNavigationPath(event.target.checked);
  };

  const handleDebugChange = (event) => {
    setDebug(event.target.checked);
  };

  const handleShowShapefileLinesChange = (event) => {
    setShowShapefileLines(event.target.checked);
  };

  const defaultCaptureName = () => {
    const base = (tifname || imageName || 'capture').replace(/\.(tif|tiff|jpg|jpeg|png)$/i, '');
    const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    return `${base}_${ts}`;
  };

  const openCaptureDialog = () => {
    setCaptureFileName(defaultCaptureName());
    setIsCaptureDialogOpen(true);
  };

  const captureHighResScreenshot = async (filename) => {
    const img = imageRef.current;
    const svg = svgRef.current;
    if (!img || !svg) {
      alert('Image not ready yet.');
      return;
    }
    // Use the displayed image's natural (thumbnail) dimensions as the canvas size.
    // The original TIF can be huge (>16384px) and exceed browser canvas limits,
    // which causes toBlob to return null.
    const MAX_CANVAS_DIM = 8192;
    let width = img.naturalWidth || originalImageSize?.width || 0;
    let height = img.naturalHeight || originalImageSize?.height || 0;
    if (!width || !height) {
      alert('Image not ready yet.');
      return;
    }
    const scaleDown = Math.min(1, MAX_CANVAS_DIM / Math.max(width, height));
    width = Math.round(width * scaleDown);
    height = Math.round(height * scaleDown);

    const svgClone = svg.cloneNode(true);
    svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    svgClone.setAttribute('width', width);
    svgClone.setAttribute('height', height);
    const svgString = new XMLSerializer().serializeToString(svgClone);
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);

    try {
      const overlayImg = await new Promise((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error('overlay decode failed'));
        el.src = svgUrl;
      });

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      ctx.drawImage(overlayImg, 0, 0, width, height);

      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      if (!blob) {
        throw new Error('canvas.toBlob returned null (canvas may be tainted or too large)');
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename || 'capture'}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('capture failed:', err);
      alert('Failed to capture screenshot: ' + err.message);
    } finally {
      URL.revokeObjectURL(svgUrl);
    }
  };

  const handleConfirmCapture = async () => {
    const name = captureFileName.trim();
    setIsCaptureDialogOpen(false);
    await captureHighResScreenshot(name);
  };

  const handleWorkspaceBufferChange = (event) => {
    setWorkspaceBuffer(event.target.value);
  };

  const handleNavigationRowSpacingChange = (event) => {
    setNavigationRowSpacing(event.target.value);
  };
  
  return (
    <div style={{
      // Fullscreen canvas host. Absolute inside App.js's 100vw × 100vh
      // shell so every floating panel can position itself against the
      // viewport rather than a bordered sub-region.
      position: 'absolute',
      inset: 0,
      display: 'flex',
      overflow: 'hidden',
      backgroundColor: '#111',
    }}>
      {/* Orthophoto mode only — in frame mode the transforms are already set. */}
      {!frame && (
      <GeoInfoReader
        tifFile={tifFile}
        onGsdCalculated={handleGsdCalculated}
        onGeoKeysCalculated={handleGeoKeysCalculated}
        onPixelToGeoReady={handlePixelToGeoReady}
        onUtmZoneReady={handleUtmZoneReady}
        onGeoToPixelReady={handleGeoToPixelReady}
      />
      )}

      {/* New noun-first primary toolbar (top-center). Floats over the
          canvas. Uses the existing `modes` map under the hood, so the old
          right-side ToolbarComponent keeps working in parallel until it's
          removed in a later phase. */}
      <PrimaryToolbar
        modes={modes}
        setMode={setMode}
        pathPanelOpen={pathPanelOpen}
        onTogglePathPanel={setPathPanelOpen}
      />

      {/* Top-center info strip (Scale / GSD / Datum / Zone). Sits just
          beneath the primary toolbar. Passive readout — non-interactive. */}
      <InfoBar
        gsd={gsd}
        datum={geoKeys?.GeogCitationGeoKey ?? geoKeys?.GTCitationGeoKey?.split('/')[0]?.trim() ?? null}
        zone={utmZone ?? (geoKeys?.GTCitationGeoKey?.match(/zone\s+(\d+[NS])/i)?.[1]) ?? null}
        selectMode={modes.selectMode}
      />

      {/* Top-right utility cluster: Undo/Redo + kebab menu for
          Export / Upload Shapefile / Screenshot / Clear Markers. Export
          Data still lives in the legacy ToolbarComponent (it owns the
          pickle/graph state); the kebab clicks the legacy button as a
          temporary bridge until that logic is lifted up. */}
      <UtilityCluster
        onUndo={undo}
        onRedo={redo}
        canUndo={historyIndex > 0}
        canRedo={historyIndex >= 0 && historyIndex < actionHistory.length - 1}
        onClearMarkers={clearAllMarkers}
        onUploadShapefile={handleUploadShapeFile}
        onCaptureScreenshot={openCaptureDialog}
        onExport={() => {
          // Backend's /export endpoint requires the path graph; reject
          // early with a clear toast instead of opening a dialog the user
          // can't successfully submit.
          if (!displayPath) {
            setToast({
              message: 'Click Display Path and wait for it to finish before exporting.',
              kind: 'warn',
            });
            return;
          }
          // DOM bridge to the legacy ToolbarComponent's Export Data button.
          // Keyed on `data-bridge="export-data"` so the selector survives
          // label or icon changes. Retire once the export logic is lifted.
          const exportBtn = document.querySelector('button[data-bridge="export-data"]');
          if (exportBtn) exportBtn.click();
        }}
        // View toggles — formerly five checkboxes in the legacy top-right
        // overlay. Order matters (most-used first): Display Image, Use
        // Workspace, Display Path, Show Shapefile Lines, Debug.
        toggles={[
          { key: 'displayImage', label: 'Display image', checked: displayImage, onChange: handleDisplayImageChange },
          { key: 'useWorkspace', label: 'Use workspace', checked: useWorkspace, onChange: handleUseWorkspaceChange },
          { key: 'abLoop', label: 'AB loop', checked: abLoop, onChange: handleAbLoopChange },
          { key: 'displayPath', label: 'Display path', checked: !!showNavigationPath, onChange: handleDisplayPathChange },
          { key: 'showShapefile', label: 'Show shapefile lines', checked: showShapefileLines, onChange: handleShowShapefileLinesChange },
          { key: 'lineLabels', label: 'Always show length / angle', checked: showLineLabels, onChange: (v) => setShowLineLabels(typeof v === 'boolean' ? v : !showLineLabels) },
          { key: 'debug', label: 'Debug', checked: debug, onChange: handleDebugChange },
        ]}
        shapefiles={shapefileCollection}
        shapefileOpacity={shapefileOpacity}
        onShapefileOpacityChange={setShapefileOpacity}
        onToggleShapefileVisible={(id, isVisible) => {
          setShapefileCollection(prev => prev.map(sf =>
            sf.id === id ? { ...sf, visible: isVisible } : sf
          ));
        }}
        onRemoveShapefile={(id) => {
          setShapefileCollection(prev => prev.filter(sf => sf.id !== id));
        }}
      />

      {/* Bottom-center contextual action bar. Hidden when nothing is
          selected. All handlers delegate to existing logic — this bar is
          purely a discovery/flow improvement over the mode-first UI. */}
      <ContextualActionBar
        selectedLine={selectedLine}
        selectedLinePoint={selectedLinePoint}
        selectedBoundary={selectedBoundary}
        selectedBoundaryPoint={selectedBoundaryPoint}
        selectedMeasurement={selectedMeasurement}
        completedMeasurements={completedMeasurements}
        gsd={gsd}
        onEdit={handleContextualEdit}
        onDelete={handleContextualDelete}
        onRepeat={handleContextualRepeat}
        onExtend={extendSelectedLines}
        onAssignType={handleContextualAssignType}
        onMove={hasMovableSelection ? () => setIsMoveDialogOpen(true) : null}
      />

      {/* Right-side Display Path panel. Only shown when Display Path is
          active in the primary toolbar. Drives the same state the legacy
          overlay inputs used, so the downstream calculation in
          ToolbarComponent.handleDisplayPath sees identical values. Apply
          re-uses that existing handler via a DOM bridge until a later
          phase lifts the calculation up. */}
      <DisplayPathPanel
        isOpen={pathPanelOpen}
        onClose={() => setPathPanelOpen(false)}
        turningRadius={turningRadius}
        onTurningRadiusChange={setTurningRadius}
        preStraightDistance={preStraightDistance}
        onPreStraightDistanceChange={setPreStraightDistance}
        abLoop={abLoop}
        abLoopCount={abLoopCount}
        onAbLoopCountChange={setAbLoopCount}
        minSptDistance={minSptDistance}
        onMinSptDistanceChange={setMinSptDistance}
        minSpsDistance={minSpsDistance}
        onMinSpsDistanceChange={setMinSpsDistance}
        turnType={turnType}
        onTurnTypeChange={setTurnType}
        cuspStraightenDistance={cuspStraightenDistance}
        onCuspStraightenDistanceChange={setCuspStraightenDistance}
        workspaceBuffer={workspaceBuffer}
        onWorkspaceBufferChange={setWorkspaceBuffer}
        innerBoundaryDistanceFeet={innerBoundaryDistanceFeet}
        onInnerBoundaryDistanceChange={setInnerBoundaryDistanceFeet}
        navigationRowSpacing={navigationRowSpacing}
        onNavigationRowSpacingChange={setNavigationRowSpacing}
        hasLines={lines.length > 0}
        isCalculating={pathCalc.isCalculating}
        calculateProgress={pathCalc.progress}
        onApply={() => {
          // Click the legacy "Display Path" button (inside ToolbarComponent)
          // so the existing long calculation pipeline runs unchanged. We
          // skip the click if the legacy button is already in its disabled
          // / Calculating state so a double-tap can't queue a second run.
          // The panel's own Apply button is also disabled via
          // `isCalculating`, but defensive guard here for good measure.
          if (pathCalc.isCalculating) return;
          const applyBtn = document.querySelector('button[data-bridge="display-path"]');
          if (applyBtn && !applyBtn.disabled) applyBtn.click();
        }}
      />

      {/* Repeat Line dialog — formerly rendered by ToolbarComponent and
          triggered via a DOM-bridge. Now the Contextual Action Bar's
          Repeat button flips `isRepeatLineDialogOpen` directly and the
          confirm handler below runs inline. `sourceLabel` previews
          which line will be duplicated so the user can sanity-check
          before committing. */}
      <RepeatLineDialog
        isOpen={isRepeatLineDialogOpen}
        onClose={() => { setIsRepeatLineDialogOpen(false); setRepeatPreview(null); }}
        onConfirm={(args) => { handleConfirmRepeat(args); setRepeatPreview(null); }}
        onPreviewChange={setRepeatPreview}
        gsd={gsd}
        sourceLabel={(() => {
          const { line, mode } = resolveRepeatSource();
          if (!line) return null;
          const idx = lines.findIndex(l => l.id === line.id);
          const human = idx >= 0 ? `line #${idx + 1}` : `line ${String(line.id).slice(0, 6)}`;
          return mode === 'selected' ? `selected ${human}` : `last ${human}`;
        })()}
      />

      {/* Move Selection dialog — ⌘⇧M / Ctrl+Shift+M in Select mode. Converts a
          real-world offset (mm or in) to pixels via GSD and applies it to
          every selected line / boundary / point in one shot, so the user
          can hit an exact offset instead of counting arrow-key nudges. */}
      <MoveSelectionDialog
        isOpen={isMoveDialogOpen}
        onClose={() => setIsMoveDialogOpen(false)}
        onConfirm={handleMoveSelection}
        gsd={gsd}
        selectionLabel={moveSelectionLabel}
      />

      {/* Floating "?" button — discoverable entry point to the shortcut
          help overlay. Sits bottom-right, opposite the zoom cluster.
          Keyboard: `?` (Shift+/) toggles the same overlay. */}
      <button
        type="button"
        onClick={() => setShortcutsHelpOpen(o => !o)}
        title="Keyboard shortcuts (?)"
        aria-pressed={shortcutsHelpOpen}
        style={{
          position: 'fixed',
          bottom: '22px',
          right: '22px',
          width: '36px',
          height: '36px',
          padding: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid #cfd7d0',
          borderRadius: '50%',
          backgroundColor: shortcutsHelpOpen ? '#2e7d32' : 'rgba(255,255,255,0.96)',
          color: shortcutsHelpOpen ? 'white' : '#223',
          cursor: 'pointer',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          fontFamily: 'Arial, sans-serif',
          fontSize: '1.05rem',
          fontWeight: 700,
          zIndex: 1200,
        }}
      >?</button>

      {/* Shortcut help overlay. Triggered by `?` or the floating help
          button. Kept small and close to where it was invoked (bottom
          right) — it's a reference, not a modal, so no scrim or click-
          blocking. Escape closes the existing handlers (disableAllModes
          / selection clear), and it only shows/hides via the `?` key or
          the button, so we don't need a dedicated close handler. */}
      {shortcutsHelpOpen && (
        <div
          role="dialog"
          aria-label="Keyboard shortcuts"
          style={{
            position: 'fixed',
            bottom: '70px',
            right: '22px',
            width: '280px',
            padding: '14px 16px',
            backgroundColor: 'rgba(255,255,255,0.98)',
            border: '1px solid #cfd7d0',
            borderRadius: '10px',
            boxShadow: '0 10px 28px rgba(0,0,0,0.22)',
            zIndex: 1400,
            fontFamily: 'Arial, sans-serif',
            fontSize: '0.85rem',
            color: '#223',
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 8, fontSize: '0.9rem' }}>Keyboard shortcuts</div>
          {[
            { section: 'Tools' },
            { keys: ['V'], desc: 'Pan / Select (clear mode)' },
            { keys: ['B'], desc: 'Boundary tool' },
            { keys: ['L'], desc: 'Line tool' },
            { keys: ['M'], desc: 'Measure tool' },
            { section: 'Selection' },
            { keys: ['E'], desc: 'Edit selection' },
            { keys: ['⌘/Ctrl', '⇧', 'M'], desc: 'Move selection by exact amount' },
            { keys: ['⇧', 'click'], desc: 'Add / remove from selection' },
            { keys: ['Del', 'Backspace'], desc: 'Delete selection' },
            { keys: ['Esc'], desc: 'Clear selection / cancel' },
            { section: 'View' },
            { keys: ['Space'], desc: 'Hold to pan while in Select mode' },
            { keys: ['W', 'A', 'S', 'D'], desc: 'Pan view' },
            { keys: ['⌘/Ctrl', 'Z'], desc: 'Undo' },
            { keys: ['⌘/Ctrl', '⇧', 'Z'], desc: 'Redo' },
            { keys: ['?'], desc: 'Toggle this overlay' },
          ].map((row, i) => {
            if (row.section) {
              return (
                <div key={`s-${i}`} style={{
                  fontSize: '0.68rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: '#667',
                  fontWeight: 700,
                  marginTop: i === 0 ? 0 : 10,
                  marginBottom: 4,
                }}>{row.section}</div>
              );
            }
            return (
              <div key={`k-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0' }}>
                <div style={{ display: 'flex', gap: 3, flex: '0 0 110px' }}>
                  {row.keys.map((k, ki) => (
                    <kbd key={ki} style={{
                      display: 'inline-block',
                      minWidth: 20,
                      padding: '1px 6px',
                      border: '1px solid #cfd7d0',
                      borderBottomWidth: 2,
                      borderRadius: 4,
                      backgroundColor: '#f7f9f7',
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: '#223',
                      textAlign: 'center',
                    }}>{k}</kbd>
                  ))}
                </div>
                <div style={{ color: '#334', fontSize: '0.8rem' }}>{row.desc}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Transient toast banner (used by Extend Lines and similar features). */}
      {toast && (
        <div style={{
          position: 'absolute',
          top: 10,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 3000,
          background: toast.kind === 'warn' ? 'rgba(200, 80, 0, 0.95)' : 'rgba(40, 90, 40, 0.95)',
          color: 'white',
          padding: '8px 16px',
          borderRadius: 6,
          fontSize: '0.9rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}>
          <span>{toast.message}</span>
          <button
            onClick={() => setToast(null)}
            style={{ background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.6)', borderRadius: 4, fontSize: '0.8rem', padding: '2px 6px', cursor: 'pointer', margin: 0 }}
          >×</button>
        </div>
      )}

      {/* Upload Section Modal */}
      {showUploadSection && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '10px',
            border: '2px solid #333',
            minWidth: '400px',
            textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#333' }}>Upload Shape File</h3>
            
            <div style={{
              border: '2px dashed #ccc',
              padding: '20px',
              margin: '10px 0',
              borderRadius: '5px',
              backgroundColor: '#f9f9f9'
            }}>
              <input
                type="file"
                onChange={handleFileSelect}
                accept=".shp,.zip,.geojson"
                style={{
                  display: 'block',
                  margin: '0 auto 10px auto',
                  padding: '5px'
                }}
              />
              {selectedFile && (
                <p style={{ margin: '10px 0', color: '#333', fontSize: '14px' }}>
                  Selected: {selectedFile.name}
                </p>
              )}
              <p style={{ margin: '5px 0', fontSize: '12px', color: '#666' }}>
                Supported format: .shp
              </p>
            </div>
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '20px' }}>
              <button
                onClick={handleFileUpload}
                disabled={!selectedFile}
                style={{
                  padding: '8px 16px',
                  backgroundColor: selectedFile ? '#007bff' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: selectedFile ? 'pointer' : 'not-allowed',
                  fontSize: '14px'
                }}
              >
                Upload
              </button>
              <button
                onClick={handleCancelUpload}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ width: '100%', height: '100%', position: 'relative' }}>
        <ZoomPanComponent
          image={image}
          backdrop={backdrop}
          drawPreview={drawPreview}
          showLineLabels={showLineLabels}
          imageRef={imageRef}
          svgRef={svgRef}
          originalImageSize={originalImageSize}
          currentScale={currentScale}
          setCurrentScale={setCurrentScale}
          handleImageInteraction={handleImageInteraction}
          handleMouseMove={handleMouseMove}
          lines={lines}
          boundaries={boundaries}
          selectedLine={selectedLine}
          selectedLinePoint={selectedLinePoint}
          hoveredLine={hoveredLine}
          hoveredLinePoint={hoveredLinePoint}
          hoveredBoundaryPoint={hoveredBoundaryPoint}
          flashingLineIds={flashingLineIds}
          selectedBoundary={selectedBoundary}
          selectedBoundaryPoint={selectedBoundaryPoint}
          hoveredBoundary={hoveredBoundary}
          editLinePointMode={modes.editLinePointMode}
          editLineMode={modes.editLineMode}
          editBoundaryPointMode={modes.editBoundaryPointMode}
          editBoundaryMode={modes.editBoundaryMode}
          currentMousePos={currentMousePos}
          modes={modes}
          isDialogOpen={isRepeatLineDialogOpen || isMoveDialogOpen}
          setSelectedLine={setSelectedLine}
          setSelectedBoundary={setSelectedBoundary}
          displayPath={displayPath}
          displayWorkspace={workspace}
          displayNodeDict={nodeDict}
          geoToPixel={geoToPixel}
          currentBoundary={currentBoundary}
          innerBoundaryPoints={innerBoundaryPoints}
          showNavigationPath={showNavigationPath}
          assignTypeAndGroupMode={modes.assignTypeAndGroupMode}
          displayImage={displayImage}
          debug={debug}
          showShapefileLines={showShapefileLines}
          shapefileCollection={shapefileCollection}
          shapefileOpacity={shapefileOpacity}
          measurementPoints={measurementPoints}
          completedMeasurements={completedMeasurements}
          currentMeasurement={currentMeasurement}
          selectedMeasurement={selectedMeasurement}
          setSelectedMeasurement={setSelectedMeasurement}
          hoveredMeasurement={hoveredMeasurement}
          repeatPreview={repeatPreview}
          repeatSourceLine={isRepeatLineDialogOpen ? resolveRepeatSource().line : null}
          gsd={gsd}
          // selectedLines={selectedLines}
          // setSelectedLines={setSelectedLines}
        />
        {/* Legacy top-right toggles panel + shapefile list have been
            folded into UtilityCluster's kebab menu (Display image, Use
            workspace, Display path, Show shapefile lines, Debug, plus
            the per-shapefile visibility/remove rows). Deleting the
            on-canvas overlay keeps the aerial image uncluttered and the
            new noun-first surfaces consistent. */}
      </div>
      {/* Legacy right-side toolbar is now visually retired by the
          noun-first UI (PrimaryToolbar + ContextualActionBar +
          UtilityCluster + DisplayPathPanel). We keep it MOUNTED off-canvas
          so its event handlers (Repeat Line dialog, Display Path compute,
          Export Data) remain reachable via the document.querySelector
          bridge calls in the new components. A follow-up phase will lift
          those handlers up into ImageEditor and delete this entirely. */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: '-10000px',
          top: 0,
          width: '300px', // arbitrary — only needs to be wide enough that
                          // the buttons render (they measure themselves
                          // for click() dispatch).
          height: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
        }}
      >
        <ToolbarComponent
        lines={lines}
        setLines={setLines}
        boundaries={boundaries}
        setBoundaries={setBoundaries}
        setSelectedBoundary={setSelectedBoundary}
        setSelectedBoundaryPoint={setSelectedBoundaryPoint}
        setHoveredBoundary={setHoveredBoundary}
        modes={modes}
        setMode={setMode}
        repeatLine={repeatLine}
        setRepeatLine={setRepeatLine}
        undo={undo}
        redo={redo}
        canUndo={historyIndex > 0}
        canRedo={historyIndex >= 0 && historyIndex < actionHistory.length - 1}
        addToHistory={addToHistory}
        pixelToGeo={pixelToGeo}
        utmZone={utmZone}
        imageName={imageName}
        onDisplayPath={handleDisplayPath}
        onDisplayWorkspace={handleDisplayWorkspace}
        onDisplayNodeDict={handleDisplayNodeDict}
        setCurrentBoundary={setCurrentBoundary}
        setInnerBoundaryPoints={setInnerBoundaryPoints}
        onClearAllMarkers={clearAllMarkers}
        innerBoundaryPoints={innerBoundaryPoints}
        extendSelectedLines={extendSelectedLines}
        selectedLineCount={selectedLine?.length || 0}
        setToast={setToast}
        setFlashingLineIds={setFlashingLineIds}
        handleUploadShapeFile={handleUploadShapeFile}
        setShowNavigationPath={setShowNavigationPath}
        defineRowGroups={defineRowGroups}
        assignTypeAndGroupToSelectedLine={assignTypeAndGroupToSelectedLine}
        selectedLine={selectedLine}
        editLineMode={modes.editLineMode}
        setSelectedLine={setSelectedLine}
        turningRadius={turningRadius}
        preStraightDistance={preStraightDistance}
        minSptDistance={minSptDistance}
        minSpsDistance={minSpsDistance}
        turnType={turnType}
        cuspStraightenDistance={cuspStraightenDistance}
        abLoopCount={abLoopCount}
        useWorkspace={useWorkspace}
        abLoop={abLoop}
        workspaceBuffer={workspaceBuffer}
        navigationRowSpacing={navigationRowSpacing}
        gsd={gsd}
        onClearAllMeasurements={onClearAllMeasurements}
        onCaptureScreenshot={openCaptureDialog}
        />
      </div>
      {isCaptureDialogOpen && (
        <div style={{
          position: 'fixed', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)', backgroundColor: 'white',
          padding: '2rem', border: '1px solid black', borderRadius: '10px',
          zIndex: 1000, width: '24rem', fontSize: '1rem'
        }}>
          <p style={{ margin: '0 0 0.75rem 0' }}>Rename screenshot (without extension):</p>
          <input
            type="text"
            autoFocus
            value={captureFileName}
            onChange={(e) => setCaptureFileName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleConfirmCapture();
              if (e.key === 'Escape') setIsCaptureDialogOpen(false);
            }}
            style={{ width: '100%', fontSize: '1rem', padding: '0.5rem', marginBottom: '0.75rem', boxSizing: 'border-box' }}
          />
          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.75rem' }}>
            <button
              onClick={handleConfirmCapture}
              style={{ fontSize: '1rem', padding: '0.6rem 1rem', backgroundColor: '#4CAF50', borderRadius: '5px' }}
            >
              Capture
            </button>
            <button
              onClick={() => setIsCaptureDialogOpen(false)}
              style={{ fontSize: '1rem', padding: '0.6rem 1rem', backgroundColor: '#f44336', borderRadius: '5px' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(ImageEditor);